
import React, { useEffect, useMemo, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { fi } from 'date-fns/locale';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiChevronDown, FiChevronUp, FiCopy } from 'react-icons/fi';
import useAuth from '../hooks/useAuth';
import useAxios, { BASE_URL } from '../api/axios';
import { toast } from 'react-toastify';
import MatchPlayRulesModal from './MatchPlayRulesModal';
import CourseDialog from './CourseDialog';
import { createEmptyCourseForm, normalizeCourseList } from '../utils/courseUtils';
import {
  calculatePayoutPool,
  calculatePrizePreview,
  formatMoney,
  getPrizeDistributionBuckets,
  isPayoutPrizeType,
  normalizePrizeSettingsForUi,
  parseMoneyValue,
} from '../utils/competitionFinance';

const EXPANDED_SECTIONS_STORAGE_PREFIX = 'competitionCardExpandedSections:';
const DEFAULT_EXPANDED_SECTIONS = {
  status: false,
  info: false,
  organizer: false,
  schedule: false,
  courses: false,
  payments: false,
  prizes: false,
  participants: false,
};

const formatDateTime = (value) => {
  if (!value) {
    return 'Ei asetettu';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString('fi-FI', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDate = (value) => {
  if (!value) {
    return 'Ei asetettu';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('fi-FI', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatCourseLabel = (course) => {
  if (!course) {
    return 'Ei valittu';
  }

  const locationParts = [course.municipality, course.region].filter(Boolean);
  return locationParts.length ? `${course.name} (${locationParts.join(', ')})` : course.name;
};

const toDateTimePickerValue = (value) => {
  if (!value) {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toDateTimePickerPayloadValue = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return '';
  }

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  const hours = String(value.getHours()).padStart(2, '0');
  const minutes = String(value.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const genderLabel = (gender) => {
  if (gender === 'male') return 'Miehet';
  if (gender === 'female') return 'Naiset';
  return 'Avoin';
};

const ageLabel = (ageOperator, ageYear) => {
  if (ageOperator === 'lt' && ageYear) return `s. ≤ ${ageYear}`;
  if (ageOperator === 'gt' && ageYear) return `s. ≥ ${ageYear}`;
  return null;
};

const getCurrentUserID = (auth) => {
  const possibleUserID = auth?.user?.userID
    ?? auth?.user?.ID
    ?? auth?.user?.userObj?.userID
    ?? auth?.user?.userObj?.ID
    ?? null;

  return possibleUserID !== null && possibleUserID !== undefined
    ? Number(possibleUserID)
    : null;
};

const hasTimestampValue = (value) => typeof value === 'string'
  ? value.trim() !== '' && value !== '0000-00-00 00:00:00'
  : Boolean(value);

const getPairUserID = (pair, slot) => Number(pair?.[`userID${slot}`] ?? pair?.[`metrixUserID${slot}`] ?? -1);

const getUserSlot = (pair, userID) => {
  if (!pair || userID === null || userID === undefined) {
    return null;
  }

  if (getPairUserID(pair, 1) === Number(userID)) {
    return 1;
  }

  if (getPairUserID(pair, 2) === Number(userID)) {
    return 2;
  }

  return null;
};

const getWinnerUserID = (pair) => {
  const winner = pair?.winnerUserID ?? null;

  if (winner === null || winner === undefined || Number(winner) === 0) {
    return null;
  }

  return Number(winner);
};

const isPairStarted = (pair) => hasTimestampValue(pair?.started);

const isPairEnded = (pair) => hasTimestampValue(pair?.ended);

const isPairFullyConfirmed = (pair) => hasTimestampValue(pair?.user1Comfirmed) && hasTimestampValue(pair?.user2Comfirmed);

const getScoringButtonLabel = (pair) => {
  if (isPairEnded(pair) && !isPairFullyConfirmed(pair)) {
    return 'Vahvista';
  }

  if (isPairStarted(pair) && !isPairEnded(pair)) {
    return 'Jatka';
  }

  return 'Aloita';
};

const didUserLosePair = (pair, userID) => {
  const winnerUserID = getWinnerUserID(pair);
  return winnerUserID !== null && Number(winnerUserID) !== Number(userID);
};

const getCurrentUserConfirmedPair = (pair, userID) => {
  const userSlot = getUserSlot(pair, userID);

  if (userSlot === 1) {
    return hasTimestampValue(pair?.user1Comfirmed);
  }

  if (userSlot === 2) {
    return hasTimestampValue(pair?.user2Comfirmed);
  }

  return false;
};

const getAutoConfirmedPlayerSummary = (pair) => {
  if (!pair) {
    return '';
  }

  const players = [];

  if (hasTimestampValue(pair?.user1AutoConfirmedAt)) {
    players.push(String(pair?.user1 ?? '').trim() || `Pelaaja #${getPairUserID(pair, 1)}`);
  }

  if (hasTimestampValue(pair?.user2AutoConfirmedAt)) {
    players.push(String(pair?.user2 ?? '').trim() || `Pelaaja #${getPairUserID(pair, 2)}`);
  }

  return players.join(' ja ');
};

const hasResolvedOpponent = (pair, userSlot) => {
  if (!pair || !userSlot) {
    return false;
  }

  const opponentSlot = userSlot === 1 ? 2 : 1;
  return getPairUserID(pair, opponentSlot) !== -1;
};

const didUserWinPair = (pair, userID) => {
  const winnerUserID = getWinnerUserID(pair);
  return winnerUserID !== null && Number(winnerUserID) === Number(userID);
};

const getSourcePairNumber = (pair, userSlot) => {
  if (!pair || !userSlot) {
    return null;
  }

  const currentPairNumber = Number(pair.pair ?? 0);

  if (!currentPairNumber || Number(pair.level ?? 0) < 1) {
    return null;
  }

  return userSlot === 1
    ? currentPairNumber * 2 - 1
    : currentPairNumber * 2;
};

const addBracketFocusTarget = (focusTargets, level, pairNumber) => {
  const numericLevel = Number(level ?? 0);
  const numericPairNumber = Number(pairNumber ?? 0);

  if (numericLevel <= 0 || numericPairNumber <= 0) {
    return;
  }

  const alreadyExists = focusTargets.some(
    (target) => Number(target?.level ?? 0) === numericLevel && Number(target?.pairNumber ?? 0) === numericPairNumber
  );

  if (!alreadyExists) {
    focusTargets.push({ level: numericLevel, pairNumber: numericPairNumber });
  }
};

const buildBracketFocusTargets = (pair, userID) => {
  if (!pair || userID === null || userID === undefined) {
    return [];
  }

  const focusTargets = [];
  const pairLevel = Number(pair?.level ?? 0);
  const pairNumber = Number(pair?.pair ?? 0);

  addBracketFocusTarget(focusTargets, pairLevel, pairNumber);

  const userSlot = getUserSlot(pair, userID);
  const opponentSlot = userSlot === 1 ? 2 : userSlot === 2 ? 1 : null;
  const opponentSourcePairNumber = opponentSlot ? getSourcePairNumber(pair, opponentSlot) : null;
  const opponentSourceLevel = pairLevel > 0 ? pairLevel + 1 : 0;

  if (opponentSourceLevel > 0 && opponentSourcePairNumber) {
    addBracketFocusTarget(focusTargets, opponentSourceLevel, opponentSourcePairNumber);
    addBracketFocusTarget(focusTargets, opponentSourceLevel + 1, opponentSourcePairNumber * 2 - 1);
    addBracketFocusTarget(focusTargets, opponentSourceLevel + 1, opponentSourcePairNumber * 2);
  }

  return focusTargets;
};

const getSourcePairNames = (pairs, pair, userSlot) => {
  const sourcePairNumber = getSourcePairNumber(pair, userSlot);
  const sourceLevel = Number(pair?.level ?? 0) + 1;

  if (!sourcePairNumber || !sourceLevel || !Array.isArray(pairs)) {
    return null;
  }

  const sourcePair = pairs.find(
    (candidate) => Number(candidate?.level ?? 0) === sourceLevel && Number(candidate?.pair ?? 0) === sourcePairNumber
  );

  if (!sourcePair) {
    return null;
  }

  const sourceUser1 = String(sourcePair?.user1 ?? '').trim();
  const sourceUser2 = String(sourcePair?.user2 ?? '').trim();
  const sourceUserID1 = getPairUserID(sourcePair, 1);
  const sourceUserID2 = getPairUserID(sourcePair, 2);

  const leftName = sourceUser1 || (sourceUserID1 > 0 ? `Pelaaja #${sourceUserID1}` : 'Ratkeaa myöhemmin');
  const rightName = sourceUser2 || (sourceUserID2 > 0 ? `Pelaaja #${sourceUserID2}` : 'Ratkeaa myöhemmin');

  return `${leftName} / ${rightName}`;
};

const getRoundTitle = (level, maxLevel) => {
  const numericLevel = Number(level ?? 0);
  const numericMaxLevel = Number(maxLevel ?? 0);

  if (!numericLevel || !numericMaxLevel) {
    return null;
  }

  if (numericLevel === 1) {
    return 'Finaali';
  }

  if (numericLevel === 2) {
    return 'Semifinaali';
  }

  if (numericLevel === 3) {
    return 'Neljännesfinaali';
  }

  const displayRound = numericMaxLevel - numericLevel + 1;

  return `Kierros ${displayRound}`;
};

const formatPercentage = (value, total) => {
  if (!total) {
    return '0 %';
  }

  return `${Math.round((value / total) * 100)} %`;
};

const getPlacementLabel = (level) => {
  const numericLevel = Number(level ?? 0);

  if (!numericLevel) {
    return 'Ei tiedossa';
  }

  if (numericLevel === 1) {
    return '2.';
  }

  const startPlacement = Math.pow(2, numericLevel - 1) + 1;
  const endPlacement = Math.pow(2, numericLevel);

  return `${startPlacement}-${endPlacement}.`;
};

const parsePrizeSettingsValue = (value) => {
  if (!value) {
    return { payoutAmount: '', prizeType: 'cash', rules: [] };
  }

  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return { payoutAmount: '', prizeType: 'cash', rules: [] };
    }
  }

  if (typeof value === 'object') {
    return value;
  }

  return { payoutAmount: '', prizeType: 'cash', rules: [] };
};

const getApplicablePrizeRule = (rules, playerCount) => {
  const numericPlayerCount = Number(playerCount || 0);

  if (!Array.isArray(rules) || numericPlayerCount <= 0) {
    return null;
  }

  return rules
    .filter((rule) => Number(rule?.minPlayers || 0) <= numericPlayerCount)
    .sort((left, right) => Number(right?.minPlayers || 0) - Number(left?.minPlayers || 0))[0] ?? null;
};

const getFinishedCompetitionStats = (pairs, userID) => {
  if (!Array.isArray(pairs) || userID === null || userID === undefined) {
    return null;
  }

  const userPairs = pairs.filter((pair) => getUserSlot(pair, userID) !== null);

  if (userPairs.length === 0) {
    return null;
  }

  let playedHoles = 0;
  let wonHoles = 0;
  let lostHoles = 0;
  let drawHoles = 0;

  userPairs.forEach((pair) => {
    const holeResults = Array.isArray(pair?.holeResults) ? pair.holeResults : [];

    holeResults.forEach((entry) => {
      const winnerUserID = Number(entry?.winnerUserID ?? -9999);
      playedHoles += 1;

      if (winnerUserID === Number(userID)) {
        wonHoles += 1;
      } else if (winnerUserID === -1) {
        drawHoles += 1;
      } else {
        lostHoles += 1;
      }
    });
  });

  return {
    placement: getPlacementLabel(
      userPairs
        .filter((pair) => didUserLosePair(pair, userID))
        .sort((left, right) => Number(left.level ?? 999) - Number(right.level ?? 999))[0]?.level
    ),
    playedHoles,
    wonHoles,
    lostHoles,
    drawHoles,
    wonPercentage: formatPercentage(wonHoles, playedHoles),
    lostPercentage: formatPercentage(lostHoles, playedHoles),
    drawPercentage: formatPercentage(drawHoles, playedHoles),
  };
};

const getNextOpponentInfo = (pairs, userID) => {
  if (userID === null || userID === undefined) {
    return null;
  }

  if (!Array.isArray(pairs) || pairs.length === 0) {
    return {
      canStart: false,
      contactAvailable: false,
      pair: null,
      roundLabel: null,
      value: 'Parit eivät ole vielä tiedossa',
    };
  }

  const maxLevel = pairs.reduce(
    (highestLevel, pair) => Math.max(highestLevel, Number(pair?.level ?? 0)),
    0
  );

  const userPairs = pairs.filter((pair) => getUserSlot(pair, userID) !== null);

  const surrenderedPair = [...userPairs]
    .filter((pair) => Number(pair?.surrenderedByUserID ?? 0) === Number(userID))
    .sort((left, right) => Number(left.level ?? 999) - Number(right.level ?? 999))[0] ?? null;

  if (surrenderedPair) {
    const surrenderedUserSlot = getUserSlot(surrenderedPair, userID);
    const opponentSlot = surrenderedUserSlot === 1 ? 2 : 1;
    const opponentName = surrenderedPair?.[`user${opponentSlot}`];
    const opponentUserID = getPairUserID(surrenderedPair, opponentSlot);
    const opponentLabel = opponentName && String(opponentName).trim() !== ''
      ? opponentName
      : opponentUserID > 0
        ? `Pelaaja #${opponentUserID}`
        : 'Vastustaja';

    return {
      canStart: false,
      contactAvailable: false,
      pair: surrenderedPair,
      roundLabel: getRoundTitle(surrenderedPair?.level, maxLevel),
      value: opponentUserID > 0
        ? `${opponentLabel} etenee jatkoon luovutuksella`
        : 'Luovutit ottelun. Vastustaja etenee jatkoon, kun hän selviää.',
      status: 'finished',
      isSurrendered: true,
    };
  }

  const latestUserRoundLevel = userPairs.reduce((lowestLevel, pair) => {
    const level = Number(pair?.level ?? 999);
    return Math.min(lowestLevel, level);
  }, 999);

  const activePairs = pairs
    .filter((pair) => (
      getUserSlot(pair, userID) !== null
      && getWinnerUserID(pair) === null
      && Number(pair?.level ?? 999) <= latestUserRoundLevel
    ))
    .sort((left, right) => Number(left.level ?? 999) - Number(right.level ?? 999));

  let nextPair = activePairs[0] ?? null;
  let nextPairUserSlot = nextPair ? getUserSlot(nextPair, userID) : null;

  const latestWonPair = [...pairs]
    .filter((pair) => didUserWinPair(pair, userID))
    .sort((left, right) => Number(left.level ?? 999) - Number(right.level ?? 999))[0] ?? null;

  const latestResolvedPair = [...pairs]
    .filter((pair) => getUserSlot(pair, userID) !== null && getWinnerUserID(pair) !== null)
    .sort((left, right) => Number(left.level ?? 999) - Number(right.level ?? 999))[0] ?? null;

  const latestResolvedLevel = Number(latestResolvedPair?.level ?? 999);

  if (nextPair && latestResolvedLevel < 999 && Number(nextPair?.level ?? 999) > latestResolvedLevel) {
    nextPair = null;
    nextPairUserSlot = null;
  }

  if (
    latestWonPair
    && (!nextPair || (nextPairUserSlot && !hasResolvedOpponent(nextPair, nextPairUserSlot) && Number(nextPair?.level ?? 999) > Number(latestWonPair?.level ?? 999) - 1))
  ) {
    const expectedLevel = Number(latestWonPair.level ?? 0) - 1;

    if (expectedLevel > 0) {
      const expectedPairNumber = Math.ceil(Number(latestWonPair.pair ?? 0) / 2);
      const expectedUserSlot = 2 - (Number(latestWonPair.pair ?? 0) % 2);
      const expectedPair = pairs.find(
        (pair) => Number(pair?.level ?? 0) === expectedLevel && Number(pair?.pair ?? 0) === expectedPairNumber
      ) ?? null;

      if (expectedPair && getWinnerUserID(expectedPair) === null) {
        nextPair = expectedPair;
        nextPairUserSlot = expectedUserSlot;
      } else if (!expectedPair) {
        const roundLabel = getRoundTitle(expectedLevel, maxLevel);
        const virtualPair = { level: expectedLevel, pair: expectedPairNumber };
        const sourcePairNames = getSourcePairNames(pairs, virtualPair, expectedUserSlot);

        return {
          canStart: false,
          contactAvailable: false,
          pair: null,
          roundLabel,
          value: sourcePairNames || 'Seuraava vastustaja ratkeaa myöhemmin',
          status: 'pending',
        };
      }
    }
  }

  if (!nextPair) {
    if (latestResolvedPair && didUserLosePair(latestResolvedPair, userID)) {
      return {
        canStart: false,
        contactAvailable: false,
        pair: latestResolvedPair,
        roundLabel: getRoundTitle(latestResolvedPair?.level, maxLevel),
        value: 'Pelisi on päättynyt',
        status: 'finished',
      };
    }

    return null;
  }

  const userSlot = nextPairUserSlot ?? getUserSlot(nextPair, userID);
  const opponentSlot = userSlot === 1 ? 2 : 1;
  const opponentName = nextPair?.[`user${opponentSlot}`];
  const opponentUserID = getPairUserID(nextPair, opponentSlot);
  const roundLabel = getRoundTitle(nextPair?.level, maxLevel);

  if (opponentName && String(opponentName).trim() !== '' && opponentUserID !== -1) {
    return {
      canStart: Boolean(nextPair?.canStartNow),
      contactAvailable: true,
      pair: nextPair,
      roundLabel,
      value: opponentName,
      status: 'active',
    };
  }

  if (opponentUserID !== -1) {
    return {
      canStart: Boolean(nextPair?.canStartNow),
      contactAvailable: true,
      pair: nextPair,
      roundLabel,
      value: `Pelaaja #${opponentUserID}`,
      status: 'active',
    };
  }

  const sourcePairNumber = getSourcePairNumber(nextPair, opponentSlot);
  const sourcePairNames = getSourcePairNames(pairs, nextPair, opponentSlot);

  if (sourcePairNames) {
    return {
      canStart: false,
      contactAvailable: false,
      pair: nextPair,
      roundLabel,
      value: sourcePairNames,
      status: 'pending',
    };
  }

  if (sourcePairNumber !== null) {
    return {
      canStart: false,
      contactAvailable: false,
      pair: nextPair,
      roundLabel,
      value: 'Tuleva parisi ei ole vielä ratkennut.',
      showBracketLink: true,
      status: 'pending',
    };
  }

  return {
    canStart: false,
    contactAvailable: false,
    pair: nextPair,
    roundLabel,
    value: 'Ei ole vielä ratkennut',
    status: 'pending',
  };
};

const isClassEligible = (cls, user) => {
  if (!user) return false;
  // Gender check
  if (cls.gender === 'male' && user.sex !== 'Mies') return false;
  if (cls.gender === 'female' && user.sex !== 'Nainen') return false;
  // Age check (user.year = birth year)
  const birthYear = user.year ? parseInt(user.year, 10) : null;
  if (cls.ageOperator === 'lt' && cls.ageYear && birthYear) {
    if (birthYear > parseInt(cls.ageYear, 10)) return false;
  }
  if (cls.ageOperator === 'gt' && cls.ageYear && birthYear) {
    if (birthYear < parseInt(cls.ageYear, 10)) return false;
  }
  return true;
};

const CompetitionCard = ({ competition, canEditCompetition = false, isRegistered = false, registeredClassID = null, registrationPaymentReferenceNumber = '', isExpanded = false, onExpand, onClose, onRegister, onCancelRegistration, onUpdateRegistration }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isAuthenticated, auth } = useAuth();
  const [showClassModal, setShowClassModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreatePairsModal, setShowCreatePairsModal] = useState(false);
  const [showStartCompetitionModal, setShowStartCompetitionModal] = useState(false);
  const [showOrganizerMessageModal, setShowOrganizerMessageModal] = useState(false);
  const [showSurrenderModal, setShowSurrenderModal] = useState(false);
  const [organizerMessageSubject, setOrganizerMessageSubject] = useState('');
  const [organizerMessageBody, setOrganizerMessageBody] = useState('');
  const [organizerMessageSending, setOrganizerMessageSending] = useState(false);
  const [surrenderSubmitting, setSurrenderSubmitting] = useState(false);
  const [selectedClassID, setSelectedClassID] = useState(null);
  const [participants, setParticipants] = useState(null);
  const [canManagePayments, setCanManagePayments] = useState(false);
  const [paymentUpdatingUserID, setPaymentUpdatingUserID] = useState(null);
  const [participantSearch, setParticipantSearch] = useState('');
  const [participantPaymentFilter, setParticipantPaymentFilter] = useState('all');
  const [pairs, setPairs] = useState([]);
  const [pairsLoaded, setPairsLoaded] = useState(false);
  const [pairsError, setPairsError] = useState('');
  const [pairsLoading, setPairsLoading] = useState(false);
  const [startCompetitionSubmitting, setStartCompetitionSubmitting] = useState(false);
  const [competitionStartDateOverride, setCompetitionStartDateOverride] = useState(null);
  const [opponentContact, setOpponentContact] = useState(null);
  const [opponentContactLoading, setOpponentContactLoading] = useState(false);
  const [opponentContactError, setOpponentContactError] = useState('');
  const [isOpponentModalOpen, setIsOpponentModalOpen] = useState(false);
  const [helpModalTopic, setHelpModalTopic] = useState('matchPlayRules');
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [pairCourseOptions, setPairCourseOptions] = useState([]);
  const [showPairCoursePicker, setShowPairCoursePicker] = useState(false);
  const [pairCourseForm, setPairCourseForm] = useState({ primaryCourseID: '', backupCourseID: '' });
  const [pairProposedTime, setPairProposedTime] = useState('');
  const [pairPlanningSubmitting, setPairPlanningSubmitting] = useState('');
  const [pairCourseDialogOpen, setPairCourseDialogOpen] = useState(false);
  const [pairCourseDialogForm, setPairCourseDialogForm] = useState(createEmptyCourseForm());
  const [pairCourseDialogSaving, setPairCourseDialogSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState(DEFAULT_EXPANDED_SECTIONS);
  const api = useAxios();
  const currentUserID = useMemo(() => getCurrentUserID(auth), [auth]);
  const isUserAuthenticated = Boolean(auth?.user);
  const competitionOrganizerUsers = useMemo(
    () => (Array.isArray(competition?.competitionOrganizerUsers) ? competition.competitionOrganizerUsers : []),
    [competition?.competitionOrganizerUsers]
  );
  const organizerName = competition?.organizerName || 'Järjestäjä puuttuu';
  const organizerLogoUrl = useMemo(() => {
    const rawLogoUrl = String(competition?.organizerLogoUrl || '').trim();

    if (!rawLogoUrl) {
      return '';
    }

    if (/^https?:\/\//i.test(rawLogoUrl)) {
      return rawLogoUrl;
    }

    return `${BASE_URL}${rawLogoUrl.startsWith('/') ? rawLogoUrl : `/${rawLogoUrl}`}`;
  }, [competition?.organizerLogoUrl]);
  const competitionName = competition?.name || competition?.competitionName || 'Nimeton kilpailu';
  const roundtime = competition?.roundtime || competition?.roundTime || 'Ei asetettu';
  const startDate = competition?.startDate || competition?.startdate || competition?.start_date;
  const registrationEndDate =
    competition?.registrationEndDate || competition?.regend || competition?.registration_end;
  const effectiveStartDate = competitionStartDateOverride || startDate;
  const isRegistrationOpen = !registrationEndDate || new Date() < new Date(registrationEndDate);
  const isAdminUser = Boolean(isAdmin && isAdmin());
  const isCompetitionStarted = (() => {
    if (!effectiveStartDate) {
      return false;
    }

    const parsedStartDate = new Date(effectiveStartDate);
    return !Number.isNaN(parsedStartDate.getTime()) && parsedStartDate <= new Date();
  })();
  const expandedSectionsStorageKey = `${EXPANDED_SECTIONS_STORAGE_PREFIX}${Number(competition?.competitionID ?? 0)}`;

  useEffect(() => {
    if (!isExpanded || !competition?.competitionID) {
      setExpandedSections(DEFAULT_EXPANDED_SECTIONS);
      return;
    }

    try {
      const storedValue = localStorage.getItem(expandedSectionsStorageKey);

      if (!storedValue) {
        setExpandedSections(DEFAULT_EXPANDED_SECTIONS);
        return;
      }

      const parsedValue = JSON.parse(storedValue);

      if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
        setExpandedSections(DEFAULT_EXPANDED_SECTIONS);
        return;
      }

      setExpandedSections({
        ...DEFAULT_EXPANDED_SECTIONS,
        ...parsedValue,
      });
    } catch {
      setExpandedSections(DEFAULT_EXPANDED_SECTIONS);
    }
  }, [expandedSectionsStorageKey, isExpanded, competition?.competitionID]);

  useEffect(() => {
    if (!isExpanded || !competition?.competitionID) {
      return;
    }

    const hasOpenSections = Object.values(expandedSections).some(Boolean);

    if (!hasOpenSections) {
      localStorage.removeItem(expandedSectionsStorageKey);
      return;
    }

    localStorage.setItem(expandedSectionsStorageKey, JSON.stringify(expandedSections));
  }, [expandedSections, expandedSectionsStorageKey, isExpanded, competition?.competitionID]);

  useEffect(() => {
    if (!isExpanded || !competition?.competitionID) return;
    let cancelled = false;
    api.post('/getCompetitionRegistrations', JSON.stringify({ competitionID: competition.competitionID }), {
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => {
        if (cancelled) {
          return;
        }

        const groups = Array.isArray(res?.data?.groups)
          ? res.data.groups
          : (Array.isArray(res?.data) ? res.data : []);

        setParticipants(groups);
        setCanManagePayments(Boolean(res?.data?.canManagePayments));
      })
      .catch(() => {
        if (!cancelled) {
          setParticipants([]);
          setCanManagePayments(false);
        }
      });
    return () => { cancelled = true; };
  }, [api, auth?.accessToken, isExpanded, competition?.competitionID, isUserAuthenticated]);

  const handleTogglePaymentStatus = async (participant) => {
    const participantUserID = Number(participant?.userID ?? 0);

    if (!competition?.competitionID || participantUserID <= 0) {
      return;
    }

    const nextPaidState = !Boolean(participant?.isPaid);
    setPaymentUpdatingUserID(participantUserID);

    try {
      const response = await api.post(
        '/updateCompetitionRegistrationPayment.php',
        JSON.stringify({
          competitionID: Number(competition.competitionID),
          userID: participantUserID,
          isPaid: nextPaidState,
        }),
        { headers: { 'Content-Type': 'application/json' } }
      );

      const updatedRegistration = response?.data?.registration ?? {
        userID: participantUserID,
        isPaid: nextPaidState,
        paidAt: nextPaidState ? new Date().toISOString() : null,
      };

      setParticipants((previousParticipants) => (
        Array.isArray(previousParticipants)
          ? previousParticipants.map((group) => ({
              ...group,
              players: Array.isArray(group?.players)
                ? group.players.map((entry) => {
                    if (typeof entry !== 'object' || entry === null || Number(entry.userID) !== participantUserID) {
                      return entry;
                    }

                    return {
                      ...entry,
                      isPaid: Boolean(updatedRegistration.isPaid),
                      paidAt: updatedRegistration.paidAt || null,
                    };
                  })
                : [],
            }))
          : previousParticipants
      ));

    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      toast.error(message || 'Maksutilan päivittäminen epäonnistui.');
    } finally {
      setPaymentUpdatingUserID(null);
    }
  };

  useEffect(() => {
    const canLoadPairs = Boolean(competition?.competitionID)
      && currentUserID !== null
      && (isRegistered || (isAdminUser && !isRegistrationOpen));

    if (!canLoadPairs) {
      setPairs([]);
      setPairsLoaded(false);
      setPairsError('');
      return;
    }

    let cancelled = false;

    const loadPairs = async () => {
      try {
        setPairsError('');
        const fetchedPairs = await loadCompetitionPairs();

        if (!cancelled) {
          setPairs(fetchedPairs);
          setPairsLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setPairs([]);
          setPairsLoaded(true);
          setPairsError('Parien haku epäonnistui.');
        }
      }
    };

    loadPairs();

    return () => {
      cancelled = true;
    };
  }, [competition?.competitionID, currentUserID, isAdminUser, isRegistered, isRegistrationOpen, registeredClassID]); // eslint-disable-line react-hooks/exhaustive-deps

  const updatePairInState = (updatedPair) => {
    if (!updatedPair?.pairID) {
      return;
    }

    setPairs((previousPairs) => (
      Array.isArray(previousPairs)
        ? previousPairs.map((pair) => (pair?.pairID === updatedPair.pairID ? updatedPair : pair))
        : previousPairs
    ));
  };

  const info = competition?.info || competition?.description || '';
  const classes = useMemo(() => (Array.isArray(competition?.classes) ? competition.classes : []), [competition]);
  const useClassSpecificEntryFee = Boolean(competition?.useClassSpecificEntryFee);
  const sharedEntryFee = parseMoneyValue(competition?.entryFee);
  const bankAccount = competition?.bankAccount || '';
  const bankRecipientName = String(competition?.bankRecipientName || '').trim();
  const enableBankPayment = Boolean(competition?.enableBankPayment);
  const mobilepayNumber = competition?.mobilepayNumber || '';
  const enableMobilepayPayment = Boolean(competition?.enableMobilepayPayment);
  const bankPaymentMessage = String(competition?.bankPaymentMessage || '').trim();
  const bankPaymentBarcode = String(competition?.bankPaymentBarcode || '').trim();
  const usePersonalReferenceNumber = Boolean(competition?.usePersonalReferenceNumber);
  const mobilepayMessage = String(competition?.mobilepayMessage || '').trim();
  const personalPaymentReferenceNumber = String(registrationPaymentReferenceNumber || '').trim();
  const bankReferenceOrMessage = usePersonalReferenceNumber
    ? personalPaymentReferenceNumber
    : bankPaymentMessage;
  const publicCourses = useMemo(() => {
    if (!Array.isArray(competition?.courses)) {
      return [];
    }

    return competition.courses
      .map((course, index) => ({
        courseID: Number(course?.courseID ?? course?.id ?? 0),
        name: String(course?.name ?? '').trim() || `Rata ${index + 1}`,
        municipality: String(course?.municipality ?? '').trim(),
        region: String(course?.region ?? '').trim(),
      }))
      .filter((course) => course.courseID > 0 || course.name)
      .sort((left, right) => left.name.localeCompare(right.name, 'fi'));
  }, [competition?.courses]);
  const competitionCourseIDs = useMemo(() => new Set(publicCourses.map((course) => Number(course.courseID))), [publicCourses]);
  const normalizedPrizeSettings = useMemo(
    () => normalizePrizeSettingsForUi(parsePrizeSettingsValue(competition?.prizeSettings)),
    [competition?.prizeSettings]
  );
  const sortedPrizeRules = useMemo(
    () => [...(normalizedPrizeSettings?.rules || [])].sort((left, right) => Number(left?.minPlayers || 0) - Number(right?.minPlayers || 0)),
    [normalizedPrizeSettings?.rules]
  );
  const user = auth?.user;
  const eligibleClasses = classes.filter((cls) => isClassEligible(cls, user));
  const participantSearchValue = participantSearch.trim().toLocaleLowerCase('fi');
  const participantSearchDigits = participantSearch.replace(/\D+/g, '');
  const visibleParticipantGroups = useMemo(() => {
    if (!Array.isArray(participants)) {
      return [];
    }

    return participants
      .map((group) => {
        const groupPlayers = Array.isArray(group?.players) ? group.players : [];
        const filteredPlayers = groupPlayers.filter((player, index) => {
          const participant = typeof player === 'object' && player !== null
            ? player
            : { userID: index, name: String(player || ''), isPaid: false, paidAt: null };
          const participantName = String(participant?.name || '').toLocaleLowerCase('fi');
          const participantReferenceNumber = String(participant?.paymentReferenceNumber || '').replace(/\D+/g, '');
          const matchesName = participantSearchValue ? participantName.includes(participantSearchValue) : false;
          const matchesReferenceNumber = participantSearchDigits ? participantReferenceNumber.includes(participantSearchDigits) : false;

          if ((participantSearchValue || participantSearchDigits) && !matchesName && !matchesReferenceNumber) {
            return false;
          }

          if (participantPaymentFilter === 'paid') {
            return Boolean(participant?.isPaid);
          }

          if (participantPaymentFilter === 'unpaid') {
            return !Boolean(participant?.isPaid);
          }

          return true;
        });

        return {
          ...group,
          players: filteredPlayers,
        };
      })
      .filter((group) => group.players.length > 0);
  }, [participantPaymentFilter, participantSearchDigits, participantSearchValue, participants]);
  const totalVisibleParticipants = useMemo(
    () => visibleParticipantGroups.reduce((sum, group) => sum + group.players.length, 0),
    [visibleParticipantGroups]
  );
  const publicClassSummaries = useMemo(() => {
    const groupMap = new Map();

    if (Array.isArray(participants)) {
      participants.forEach((group, index) => {
        const groupClassID = group?.classID ?? null;
        const key = groupClassID !== null && groupClassID !== undefined ? `id:${groupClassID}` : `group:${index}`;
        groupMap.set(key, {
          classID: groupClassID,
          className: group?.className || (groupClassID !== null && groupClassID !== undefined ? `Luokka #${groupClassID}` : 'Ei luokkaa'),
          playerCount: Array.isArray(group?.players) ? group.players.length : 0,
        });
      });
    }

    const summaries = classes.map((competitionClass, index) => {
      const classID = competitionClass?.classID ?? competitionClass?.id ?? null;
      const key = classID !== null && classID !== undefined ? `id:${classID}` : `class:${index}`;
      const matchedGroup = groupMap.get(key);
      const playerCount = matchedGroup ? matchedGroup.playerCount : Number(competitionClass?.registrationCount ?? 0);

      return {
        classID,
        className: competitionClass?.name || matchedGroup?.className || `Luokka ${index + 1}`,
        playerCount,
        entryFee: useClassSpecificEntryFee ? parseMoneyValue(competitionClass?.entryFee) : sharedEntryFee,
      };
    });

    groupMap.forEach((group) => {
      const exists = summaries.some((summary) => String(summary.classID ?? '') === String(group.classID ?? ''));
      if (!exists) {
        summaries.push({
          classID: group.classID,
          className: group.className,
          playerCount: group.playerCount,
          entryFee: sharedEntryFee,
        });
      }
    });

    return summaries;
  }, [classes, participants, sharedEntryFee, useClassSpecificEntryFee]);
  const publicPrizeStatusByClass = useMemo(() => (
    publicClassSummaries.map((summary) => {
      const applicableRule = getApplicablePrizeRule(sortedPrizeRules, summary.playerCount);
      const totalEntryFees = summary.entryFee !== null
        ? Math.round(summary.entryFee * summary.playerCount * 100) / 100
        : null;
      const payoutPool = applicableRule && totalEntryFees !== null && isPayoutPrizeType(normalizedPrizeSettings?.prizeType)
        ? calculatePayoutPool(totalEntryFees, normalizedPrizeSettings?.payoutAmount)
        : null;
      const previewRows = applicableRule && totalEntryFees !== null && isPayoutPrizeType(normalizedPrizeSettings?.prizeType)
        ? calculatePrizePreview(applicableRule, normalizedPrizeSettings?.payoutAmount, totalEntryFees)
        : [];

      return {
        ...summary,
        applicableRule,
        totalEntryFees,
        payoutPool,
        previewRows,
      };
    })
  ), [normalizedPrizeSettings?.payoutAmount, normalizedPrizeSettings?.prizeType, publicClassSummaries, sortedPrizeRules]);
  const prizeRuleSummaries = useMemo(() => (
    sortedPrizeRules.map((rule) => ({
      minPlayers: Number(rule?.minPlayers || 0),
      placements: getPrizeDistributionBuckets(rule?.winnerCount).map((bucket) => bucket.placementLabel),
      winnerCount: Number(rule?.winnerCount || 0),
    }))
  ), [sortedPrizeRules]);
  const nextOpponentInfo = useMemo(() => getNextOpponentInfo(pairs, currentUserID), [currentUserID, pairs]);
  const activePair = nextOpponentInfo?.pair ?? null;
  const activePairCourseSelection = activePair?.courseSelection ?? null;
  const activePairResolvedCourse = activePair?.resolvedCourse ?? null;
  const activePairAcceptedTimeProposal = activePair?.agreedTimeProposal ?? null;
  const shouldShowPairPlanning = Boolean(activePair) && nextOpponentInfo?.status === 'active';
  const activePairCanChooseCourse = activePair && Number(activePair?.courseChooserUserID ?? 0) === Number(currentUserID);
  const activePairNeedsCourseApproval = Boolean(activePairCourseSelection?.requiresApproval) && activePairCourseSelection?.status === 'pending';
  const selectedPairPrimaryCourseID = Number(pairCourseForm.primaryCourseID || 0);
  const hasSelectedPairPrimaryCourse = selectedPairPrimaryCourseID > 0;
  const selectedPairCourseRequiresBackup = hasSelectedPairPrimaryCourse && !competitionCourseIDs.has(selectedPairPrimaryCourseID);
  const activePairCourseChooserLabel = useMemo(() => {
    if (!activePair) {
      return '';
    }

    const chooserUserID = Number(activePair?.courseChooserUserID ?? 0);

    if (chooserUserID <= 0) {
      return 'Radan valitsija ei ole vielä tiedossa.';
    }

    if (chooserUserID === Number(currentUserID)) {
      return 'Sinä valitset radan.';
    }

    const opponentName = String(nextOpponentInfo?.value || '').trim();
    return opponentName ? `${opponentName} valitsee radan.` : 'Vastustaja valitsee radan.';
  }, [activePair, currentUserID, nextOpponentInfo?.value]);

  const loadCompetitionPairs = async () => {
    const competitionPayload = {
      competitionID: Number(competition.competitionID),
    };

    const fetchPairs = async (payload) => {
      const response = await api.post(
        '/getPairs',
        JSON.stringify(payload),
        { headers: { 'Content-Type': 'application/json' } }
      );

      return Array.isArray(response?.data) ? response.data : [];
    };

    let fetchedPairs = [];

    if (registeredClassID !== null && registeredClassID !== undefined) {
      fetchedPairs = await fetchPairs({
        ...competitionPayload,
        classID: Number(registeredClassID),
      });
    }

    if (fetchedPairs.length === 0) {
      fetchedPairs = await fetchPairs(competitionPayload);
    }

    return fetchedPairs;
  };

  useEffect(() => {
    if (!activePair) {
      setShowPairCoursePicker(false);
      setPairCourseForm({ primaryCourseID: '', backupCourseID: '' });
      setPairProposedTime('');
      return;
    }

    setPairCourseForm({
      primaryCourseID: String(activePairCourseSelection?.primaryCourse?.courseID ?? ''),
      backupCourseID: String(activePairCourseSelection?.backupCourse?.courseID ?? ''),
    });
    setPairProposedTime('');
  }, [activePair, activePairCourseSelection]);

  useEffect(() => {
    if (!isExpanded || !isUserAuthenticated || !shouldShowPairPlanning) {
      return;
    }

    let cancelled = false;

    const loadCourses = async () => {
      try {
        const response = await api.post('/getCourses.php', JSON.stringify({}), {
          headers: { 'Content-Type': 'application/json' },
        });

        if (cancelled) {
          return;
        }

        setPairCourseOptions(normalizeCourseList(response?.data?.courses));
      } catch {
        if (!cancelled) {
          setPairCourseOptions([]);
        }
      }
    };

    loadCourses();

    return () => {
      cancelled = true;
    };
  }, [api, isExpanded, isUserAuthenticated, shouldShowPairPlanning]);
  const scoringButtonLabel = useMemo(() => getScoringButtonLabel(nextOpponentInfo?.pair), [nextOpponentInfo?.pair]);
  const currentUserConfirmedNextPair = useMemo(
    () => getCurrentUserConfirmedPair(nextOpponentInfo?.pair, currentUserID),
    [currentUserID, nextOpponentInfo?.pair]
  );
  const finishedCompetitionStats = useMemo(() => {
    if (nextOpponentInfo?.status !== 'finished') {
      return null;
    }

    return getFinishedCompetitionStats(pairs, currentUserID);
  }, [currentUserID, nextOpponentInfo?.status, pairs]);
  const shouldShowScoringButton = useMemo(() => {
    if (!pairsLoaded || !nextOpponentInfo?.canStart) {
      return false;
    }

    if (scoringButtonLabel === 'Vahvista' && currentUserConfirmedNextPair) {
      return false;
    }

    return true;
  }, [currentUserConfirmedNextPair, nextOpponentInfo?.canStart, pairsLoaded, scoringButtonLabel]);
  const shouldShowWaitingForOpponentConfirmation = useMemo(() => {
    if (!pairsLoaded || !nextOpponentInfo?.pair) {
      return false;
    }

    return isPairEnded(nextOpponentInfo.pair)
      && !isPairFullyConfirmed(nextOpponentInfo.pair)
      && currentUserConfirmedNextPair;
  }, [currentUserConfirmedNextPair, nextOpponentInfo?.pair, pairsLoaded]);

  const handleRegisterClick = () => {
    if (classes.length === 0) {
      onRegister(competition.competitionID, null);
    } else {
      setSelectedClassID(null);
      setShowClassModal(true);
    }
  };

  const handleModalConfirm = () => {
    if (selectedClassID === null) return;
    setShowClassModal(false);
    onRegister(competition.competitionID, selectedClassID);
  };

  const handleEditModalOpen = (e) => {
    e.stopPropagation();
    setSelectedClassID(registeredClassID !== null ? Number(registeredClassID) : null);
    setShowEditModal(true);
  };

  const handleEditModalUpdate = () => {
    setShowEditModal(false);
    onUpdateRegistration(competition.competitionID, selectedClassID);
  };

  const handleEditModalCancel = () => {
    setShowEditModal(false);
    onCancelRegistration(competition.competitionID);
  };

  const handleCreatePairs = (event) => {
    event?.stopPropagation?.();
    setShowCreatePairsModal(true);
  };

  const handleCloseCreatePairsModal = () => {
    if (pairsLoading) {
      return;
    }

    setShowCreatePairsModal(false);
  };

  const handleConfirmCreatePairs = async () => {
    setPairsLoading(true);

    try {
      await api.post(
        '/admin/CreatePairs',
        JSON.stringify({ competitionID: competition.competitionID }),
        { headers: { 'Content-Type': 'application/json' } }
      );
      const fetchedPairs = await loadCompetitionPairs();
      setPairs(fetchedPairs);
      setPairsLoaded(true);
      setPairsError('');
      toast.success('Parit luotu onnistuneesti!');
      setShowCreatePairsModal(false);
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      toast.error(message || 'Parien luominen epäonnistui');
    } finally {
      setPairsLoading(false);
    }

  };

  const handleOpenStartCompetitionModal = (event) => {
    event?.stopPropagation?.();
    setShowStartCompetitionModal(true);
  };

  const handleCloseStartCompetitionModal = () => {
    if (startCompetitionSubmitting) {
      return;
    }

    setShowStartCompetitionModal(false);
  };

  const handleStartCompetition = async () => {
    if (!competition?.competitionID) {
      return;
    }

    setStartCompetitionSubmitting(true);

    try {
      const response = await api.post(
        '/admin/StartCompetition',
        JSON.stringify({ competitionID: Number(competition.competitionID) }),
        { headers: { 'Content-Type': 'application/json' } }
      );
      const updatedCompetition = response?.data?.competition;
      const fetchedPairs = await loadCompetitionPairs();

      setCompetitionStartDateOverride(updatedCompetition?.startDate || new Date().toISOString());
      setPairs(fetchedPairs);
      setPairsLoaded(true);
      setPairsError('');
      setShowStartCompetitionModal(false);
      toast.success('Kilpailu aloitettu ja osallistujille lähetettiin ilmoitus.');
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      toast.error(message || 'Kilpailun aloitus epäonnistui');
    } finally {
      setStartCompetitionSubmitting(false);
    }
  };

  const handleOpenOrganizerMessageModal = (event) => {
    event?.stopPropagation?.();
    setOrganizerMessageSubject('');
    setOrganizerMessageBody('');
    setShowOrganizerMessageModal(true);
  };

  const handleCloseOrganizerMessageModal = () => {
    if (organizerMessageSending) {
      return;
    }

    setShowOrganizerMessageModal(false);
  };

  const handleOpenSurrenderModal = (event) => {
    event?.stopPropagation?.();
    setShowSurrenderModal(true);
  };

  const handleCloseSurrenderModal = () => {
    if (surrenderSubmitting) {
      return;
    }

    setShowSurrenderModal(false);
  };

  const handleConfirmSurrender = async () => {
    if (!competition?.competitionID) {
      return;
    }

    setSurrenderSubmitting(true);

    try {
      await api.post(
        '/surrenderPair.php',
        JSON.stringify({
          competitionID: Number(competition.competitionID),
          ...(registeredClassID !== null && registeredClassID !== undefined ? { classID: Number(registeredClassID) } : {}),
        }),
        { headers: { 'Content-Type': 'application/json' } }
      );

      const pairsResponse = await api.post(
        '/getPairs',
        JSON.stringify({
          competitionID: Number(competition.competitionID),
          ...(registeredClassID !== null && registeredClassID !== undefined ? { classID: Number(registeredClassID) } : {}),
        }),
        { headers: { 'Content-Type': 'application/json' } }
      );

      setPairs(Array.isArray(pairsResponse?.data) ? pairsResponse.data : []);
      setPairsLoaded(true);
      setPairsError('');
      setShowSurrenderModal(false);
      toast.success('Luovutus tallennettu. Kilpailun pelisi ovat nyt pelatut.');
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      toast.error(message || 'Luovutuksen tallennus epäonnistui.');
    } finally {
      setSurrenderSubmitting(false);
    }
  };

  const handleSendOrganizerMessage = async () => {
    const subject = organizerMessageSubject.trim();
    const message = organizerMessageBody.trim();

    if (!competition?.competitionID) {
      toast.error('Kilpailun tiedot puuttuvat.');
      return;
    }

    if (!subject) {
      toast.error('Anna viestille aihe.');
      return;
    }

    if (!message) {
      toast.error('Anna viestin sisältö.');
      return;
    }

    setOrganizerMessageSending(true);

    try {
      const response = await api.post(
        '/sendMessageToCompetitionOrganizer.php',
        JSON.stringify({
          competitionID: Number(competition.competitionID),
          subject,
          message,
        }),
        { headers: { 'Content-Type': 'application/json' } }
      );

      const recipientCount = Number(response?.data?.recipientCount ?? 0);
      toast.success(
        recipientCount > 0
          ? `Viesti toimitettu kilpailun järjestäjälle (${recipientCount} vastaanottajaa).`
          : 'Viesti toimitettu kilpailun järjestäjälle.'
      );
      setShowOrganizerMessageModal(false);
      setOrganizerMessageSubject('');
      setOrganizerMessageBody('');
    } catch (err) {
      const messageText = err?.response?.data?.message || err?.response?.data?.msg;
      toast.error(messageText || 'Viestin lähettäminen epäonnistui.');
    } finally {
      setOrganizerMessageSending(false);
    }
  };

  const handleOpenResults = () => {
    navigate(`/tulokset/${competition.competitionID}`, {
      state: {
        competition,
        registeredClassID,
        returnTo: `${location.pathname}${location.search}`,
      },
    });
  };

  const handleOpenResultsWithBracketFocus = () => {
    navigate(`/tulokset/${competition.competitionID}`, {
      state: {
        competition,
        registeredClassID,
        returnTo: `${location.pathname}${location.search}`,
        bracketFocusTargets: buildBracketFocusTargets(activePair, currentUserID),
      },
    });
  };

  const handleCopyCompetitionLink = async (event) => {
    event?.stopPropagation?.();

    if (!competition?.competitionID || typeof window === 'undefined') {
      return;
    }

    const shareUrl = `${window.location.origin}/kisat/${competition.competitionID}`;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Kilpailun linkki kopioitu.');
        return;
      }

      toast.info(shareUrl);
    } catch (error) {
      toast.error('Kilpailun linkin kopiointi epäonnistui.');
    }
  };

  const handleCopyPaymentValue = async (event, value, successMessage) => {
    event?.stopPropagation?.();

    if (!value || typeof window === 'undefined') {
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(String(value));
        toast.success(successMessage);
        return;
      }

      toast.info(String(value));
    } catch (error) {
      toast.error('Kopiointi epäonnistui.');
    }
  };

  const renderCopyableField = (label, value, successMessage, options = {}) => {
    const { accent = 'slate', breakAll = false } = options;
    const valueClassName = breakAll ? 'mt-1 break-all font-semibold text-slate-900' : 'mt-1 font-semibold text-slate-900';
    const buttonClassName = accent === 'cyan'
      ? 'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-cyan-200 bg-cyan-50 text-cyan-700 transition hover:bg-cyan-100'
      : 'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-700 transition hover:bg-slate-100';

    return (
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</p>
          <p className={valueClassName}>{value || 'Ei ilmoitettu'}</p>
        </div>
        {value ? (
          <button
            type="button"
            onClick={(event) => handleCopyPaymentValue(event, value, successMessage)}
            aria-label={`Kopioi ${label.toLowerCase()}`}
            title={`Kopioi ${label.toLowerCase()}`}
            className={buttonClassName}
          >
            <FiCopy className="text-sm" />
          </button>
        ) : null}
      </div>
    );
  };

  const handleStartScoring = (e) => {
    e.stopPropagation();

    if (!competition?.competitionID || !nextOpponentInfo?.canStart || !nextOpponentInfo?.pair?.pairID) {
      return;
    }

    navigate(`/kirjaus/${competition.competitionID}/${nextOpponentInfo.pair.pairID}`, {
      state: {
        competition,
        pair: nextOpponentInfo.pair,
        registeredClassID,
      },
    });
  };

  const handleOpenOpponentContact = async (e) => {
    e.stopPropagation();

    if (!competition?.competitionID || !nextOpponentInfo?.contactAvailable) {
      return;
    }

    setIsOpponentModalOpen(true);
    setOpponentContactLoading(true);
    setOpponentContactError('');
    setOpponentContact(null);

    try {
      const response = await api.post(
        '/getNextOpponentContact',
        JSON.stringify({
          competitionID: Number(competition.competitionID),
          ...(registeredClassID !== null && registeredClassID !== undefined ? { classID: Number(registeredClassID) } : {}),
        }),
        { headers: { 'Content-Type': 'application/json' } }
      );

      setOpponentContact(response?.data ?? null);
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      setOpponentContactError(message || 'Vastustajan yhteystietojen haku epäonnistui.');
    } finally {
      setOpponentContactLoading(false);
    }
  };

  const handleCloseOpponentModal = () => {
    setIsOpponentModalOpen(false);
    setOpponentContact(null);
    setOpponentContactError('');
    setOpponentContactLoading(false);
  };

  const handleSubmitPairCourse = async () => {
    if (!activePair?.pairID) {
      return;
    }

    const primaryCourseID = Number(pairCourseForm.primaryCourseID || 0);
    const backupCourseID = pairCourseForm.backupCourseID ? Number(pairCourseForm.backupCourseID) : null;
    const requiresBackupCourse = primaryCourseID > 0 && !competitionCourseIDs.has(primaryCourseID);

    if (!primaryCourseID) {
      toast.error('Valitse rata.');
      return;
    }

    if (requiresBackupCourse && !backupCourseID) {
      toast.error('Valitse kisaan kuuluva vararata ennen tallennusta.');
      return;
    }

    setPairPlanningSubmitting('course');

    try {
      const response = await api.post('/selectPairCourse.php', JSON.stringify({
        pairID: activePair.pairID,
        primaryCourseID,
        backupCourseID,
      }), { headers: { 'Content-Type': 'application/json' } });

      updatePairInState(response?.data?.pair);
      setShowPairCoursePicker(false);
      toast.success('Rata tallennettu.');
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      toast.error(message || 'Radan tallennus epäonnistui.');
    } finally {
      setPairPlanningSubmitting('');
    }
  };

  const handleRespondPairCourse = async (approved) => {
    if (!activePair?.pairID) {
      return;
    }

    setPairPlanningSubmitting(approved ? 'approve-course' : 'reject-course');

    try {
      const response = await api.post('/respondPairCourse.php', JSON.stringify({
        pairID: activePair.pairID,
        approved,
      }), { headers: { 'Content-Type': 'application/json' } });

      updatePairInState(response?.data?.pair);
      toast.success(approved ? 'Rata hyväksytty.' : 'Ehdotettu rata hylätty. Vararata otettiin käyttöön.');
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      toast.error(message || 'Radan käsittely epäonnistui.');
    } finally {
      setPairPlanningSubmitting('');
    }
  };

  const handleProposePairTime = async () => {
    if (!activePair?.pairID || !pairProposedTime) {
      toast.error('Valitse ehdotettu peliaika.');
      return;
    }

    const proposedTime = new Date(pairProposedTime);

    if (Number.isNaN(proposedTime.getTime()) || proposedTime.getTime() < Date.now()) {
      toast.error('Ehdotettu aika ei voi olla menneisyydessä.');
      return;
    }

    setPairPlanningSubmitting('time');

    try {
      const response = await api.post('/proposePairTime.php', JSON.stringify({
        pairID: activePair.pairID,
        scheduledStart: pairProposedTime,
      }), { headers: { 'Content-Type': 'application/json' } });

      updatePairInState(response?.data?.pair);
      setPairProposedTime('');
      toast.success('Peliaikaehdotus lähetetty.');
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      toast.error(message || 'Peliaikaehdotuksen lähetys epäonnistui.');
    } finally {
      setPairPlanningSubmitting('');
    }
  };

  const handleRespondPairTime = async (proposalID, approved) => {
    if (!activePair?.pairID || !proposalID) {
      return;
    }

    setPairPlanningSubmitting(approved ? `accept-time-${proposalID}` : `reject-time-${proposalID}`);

    try {
      const response = await api.post('/respondPairTime.php', JSON.stringify({
        pairID: activePair.pairID,
        proposalID,
        approved,
      }), { headers: { 'Content-Type': 'application/json' } });

      updatePairInState(response?.data?.pair);
      toast.success(approved ? 'Peliaika hyväksytty.' : 'Peliaika hylätty.');
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      toast.error(message || 'Peliaikaehdotuksen käsittely epäonnistui.');
    } finally {
      setPairPlanningSubmitting('');
    }
  };

  const handlePairCourseDialogFieldChange = (field) => (event) => {
    const nextValue = event?.target?.value ?? '';
    setPairCourseDialogForm((previousForm) => ({
      ...previousForm,
      [field]: nextValue,
    }));
  };

  const handleSavePairCourseDialog = async () => {
    const trimmedName = pairCourseDialogForm.name.trim();
    const trimmedMunicipality = pairCourseDialogForm.municipality.trim();
    const trimmedRegion = pairCourseDialogForm.region.trim();

    if (!trimmedName || !trimmedMunicipality || !trimmedRegion) {
      toast.error('Täytä uuden radan kaikki kentät.');
      return;
    }

    setPairCourseDialogSaving(true);

    try {
      const response = await api.post('/addCource.php', JSON.stringify({
        course: {
          name: trimmedName,
          municipality: trimmedMunicipality,
          region: trimmedRegion,
        },
      }), { headers: { 'Content-Type': 'application/json' } });

      const savedCourse = response?.data?.course;
      const refreshedCoursesResponse = await api.post('/getCourses.php', JSON.stringify({}), {
        headers: { 'Content-Type': 'application/json' },
      });
      const refreshedCourses = normalizeCourseList(refreshedCoursesResponse?.data?.courses);
      setPairCourseOptions(refreshedCourses);
      setPairCourseDialogOpen(false);
      setPairCourseDialogForm(createEmptyCourseForm());

      if (savedCourse?.courseID) {
        setPairCourseForm((previousForm) => ({
          ...previousForm,
          primaryCourseID: String(savedCourse.courseID),
        }));
      }

      toast.success('Rata lisätty.');
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      toast.error(message || 'Radan lisäys epäonnistui.');
    } finally {
      setPairCourseDialogSaving(false);
    }
  };

  const classPills = classes.map((cls) => {
    const age = ageLabel(cls.ageOperator, cls.ageYear);
    const isUserClass = isRegistered && registeredClassID !== null && Number(cls.classID) === Number(registeredClassID);
    const count = cls.registrationCount ?? 0;
    return (
      <span
        key={cls.classID ?? cls.name}
        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${
          isUserClass
            ? 'bg-green-100 border-green-200 text-green-800'
            : 'bg-cyan-50 border-cyan-200 text-cyan-800'
        }`}
      >
        {cls.name}
        <span className={`font-normal ${isUserClass ? 'text-green-600' : 'text-cyan-600'}`}>
          · {genderLabel(cls.gender)}{age ? ` · ${age}` : ''}
        </span>
        <span className={`inline-flex items-center gap-0.5 ${isUserClass ? 'text-green-600' : 'text-cyan-600'}`}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3">
            <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM12.735 14c.618 0 1.093-.561.872-1.139a6.002 6.002 0 0 0-11.215 0c-.22.578.254 1.139.872 1.139h9.47Z" />
          </svg>
          {count}
        </span>
      </span>
    );
  });

  const hasRegisteredClassBadge = registeredClassID !== null
    && classes.some((cls) => Number(cls.classID) === Number(registeredClassID));

  const missingRegisteredClassPill = isRegistered && registeredClassID !== null && !hasRegisteredClassBadge ? (
    <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-800">
      Luokka ei löytynyt
      <span className="font-normal text-amber-700">· #{registeredClassID}</span>
    </span>
  ) : null;

  const compactClassPills = classes.map((cls, index) => {
    const isUserClass = isRegistered && registeredClassID !== null && Number(cls.classID) === Number(registeredClassID);

    return (
      <span
        key={cls.classID ?? cls.name ?? index}
        className={isUserClass
          ? 'inline-flex rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700'
          : 'inline-flex rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700'}
      >
        {cls.name || `Luokka ${index + 1}`}
      </span>
    );
  });

  const toggleSection = (sectionKey) => {
    setExpandedSections((previousSections) => ({
      ...previousSections,
      [sectionKey]: !previousSections[sectionKey],
    }));
  };

  const renderSection = (sectionKey, title, content, options = {}) => {
    const { subtitle = null, className = '' } = options;
    const isSectionOpen = expandedSections[sectionKey];
    const ChevronIcon = isSectionOpen ? FiChevronUp : FiChevronDown;

    return (
      <div className={isSectionOpen
        ? `rounded-2xl border border-slate-200 bg-slate-50 ${className}`.trim()
        : `border-t border-slate-200 ${className}`.trim()}>
        <button
          type="button"
          onClick={() => toggleSection(sectionKey)}
          className={isSectionOpen
            ? 'flex w-full items-center justify-between gap-3 px-4 py-3 text-left'
            : 'flex w-full items-center justify-between gap-3 px-0 py-2.5 text-left'}
        >
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{title}</p>
            {isSectionOpen && subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
          </div>
          <span className={isSectionOpen
            ? 'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 transition hover:bg-slate-100'
            : 'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-700 transition hover:bg-slate-100'}>
            <ChevronIcon className="text-lg" />
          </span>
        </button>
        {isSectionOpen ? <div className="border-t border-slate-200 px-4 py-4">{content}</div> : null}
      </div>
    );
  };

  const registrationIndicatorTitle = isRegistered
    ? 'Olet rekisteröitynyt kisaan'
    : 'Et ole rekisteröitynyt kisaan';

  const registrationIndicatorClassName = isRegistered
    ? 'inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-slate-900 bg-green-200 text-[11px] font-black text-slate-900'
    : 'inline-flex h-6 w-6 items-center justify-center rounded-full border-2 border-slate-900 bg-rose-200 text-[11px] font-black text-slate-900';

  const handleOpenHelpTopic = (topic) => {
    setHelpModalTopic(topic);
    setIsHelpModalOpen(true);
  };

  const finishedCompetitionBlock = isRegistered && nextOpponentInfo?.status === 'finished' && finishedCompetitionStats ? (
    <div className="mt-2 rounded-2xl border border-stone-300 bg-stone-50 px-3 py-3 text-sm text-slate-900">
      <p className="font-semibold text-slate-900">Pelisi on päättynyt kierroksella {nextOpponentInfo.roundLabel || 'Ei tiedossa'}.</p>
      {hasTimestampValue(nextOpponentInfo?.pair?.autoConfirmedAt) ? (
        <p className="mt-2 text-xs font-semibold text-slate-600">Tulos automaattivahvistettiin: {getAutoConfirmedPlayerSummary(nextOpponentInfo.pair) || 'järjestelmä'} ({formatDateTime(nextOpponentInfo.pair.autoConfirmedAt)}).</p>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-1.5">
        <div className="w-fit rounded-xl border border-stone-300 bg-white px-2.5 py-2 text-center text-[11px] shadow-sm">
          <p className="font-semibold uppercase tracking-[0.14em] text-slate-500">Sijoitus</p>
          <p className="mt-0.5 text-sm font-black text-slate-900">{finishedCompetitionStats.placement}</p>
        </div>
        <div className="w-fit rounded-xl border border-stone-300 bg-white px-2.5 py-2 text-center text-[11px] shadow-sm">
          <p className="font-semibold uppercase tracking-[0.14em] text-slate-500">Pelattu</p>
          <p className="mt-0.5 text-sm font-black text-slate-900">{finishedCompetitionStats.playedHoles}</p>
        </div>
        <div className="w-fit rounded-xl border border-stone-300 bg-white px-2.5 py-2 text-center text-[11px] shadow-sm">
          <p className="font-semibold uppercase tracking-[0.14em] text-slate-500">Voitot</p>
          <p className="mt-0.5 text-sm font-black text-slate-900">{finishedCompetitionStats.wonHoles} <span className="text-[10px] font-medium text-slate-500">({finishedCompetitionStats.wonPercentage})</span></p>
        </div>
        <div className="w-fit rounded-xl border border-stone-300 bg-white px-2.5 py-2 text-center text-[11px] shadow-sm">
          <p className="font-semibold uppercase tracking-[0.14em] text-slate-500">Häviöt</p>
          <p className="mt-0.5 text-sm font-black text-slate-900">{finishedCompetitionStats.lostHoles} <span className="text-[10px] font-medium text-slate-500">({finishedCompetitionStats.lostPercentage})</span></p>
        </div>
        <div className="w-fit rounded-xl border border-stone-300 bg-white px-2.5 py-2 text-center text-[11px] shadow-sm">
          <p className="font-semibold uppercase tracking-[0.14em] text-slate-500">Tasapelit</p>
          <p className="mt-0.5 text-sm font-black text-slate-900">{finishedCompetitionStats.drawHoles} <span className="text-[10px] font-medium text-slate-500">({finishedCompetitionStats.drawPercentage})</span></p>
        </div>
      </div>
    </div>
  ) : null;

  const nextOpponentBlock = isRegistered && nextOpponentInfo?.status !== 'finished' ? (
    <div className="mt-2 rounded-2xl border border-stone-300 bg-stone-50 px-3 py-2 text-sm text-slate-900">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">Seuraava vastustaja</p>
          {pairsLoaded && pairsError ? (
            <p className="mt-1 font-semibold text-rose-700">{pairsError}</p>
          ) : (
            <>
              {pairsLoaded && nextOpponentInfo?.contactAvailable ? (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={handleOpenOpponentContact}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleOpenOpponentContact(e);
                    }
                  }}
                  className="mt-1 inline-block cursor-pointer font-semibold text-slate-900 underline decoration-stone-400 underline-offset-2 hover:text-slate-700"
                >
                  {nextOpponentInfo.value}
                </span>
              ) : (
                <div className="mt-1">
                  <p className="font-semibold">
                    {pairsLoaded
                      ? nextOpponentInfo?.value ?? 'Otteluparia ei ole vielä saatavilla'
                      : 'Haetaan seuraavaa vastustajaa...'}
                  </p>
                  {pairsLoaded && nextOpponentInfo?.showBracketLink ? (
                    <button
                      type="button"
                      onClick={handleOpenResultsWithBracketFocus}
                      className="mt-1 inline-flex items-center p-0 text-sm font-semibold text-cyan-700 underline decoration-cyan-400 underline-offset-2 transition hover:text-cyan-900"
                    >
                      Katso cup-kaavio
                    </button>
                  ) : null}
                </div>
              )}
            </>
          )}
          {pairsLoaded && nextOpponentInfo?.pair && (hasTimestampValue(nextOpponentInfo.pair.started) || hasTimestampValue(nextOpponentInfo.pair.ended)) ? (
            <div className="mt-2 space-y-1 text-xs font-medium text-slate-600">
              {hasTimestampValue(nextOpponentInfo.pair.started) ? <p>Aloitettu: {formatDateTime(nextOpponentInfo.pair.started)}</p> : null}
              {hasTimestampValue(nextOpponentInfo.pair.ended) ? <p>Päättynyt: {formatDateTime(nextOpponentInfo.pair.ended)}</p> : null}
              {hasTimestampValue(nextOpponentInfo.pair.autoConfirmedAt) ? <p>Automaattivahvistettu: {getAutoConfirmedPlayerSummary(nextOpponentInfo.pair) || 'järjestelmä'} ({formatDateTime(nextOpponentInfo.pair.autoConfirmedAt)})</p> : null}
            </div>
          ) : null}

          {shouldShowPairPlanning ? (
            <div className="mt-4 rounded-2xl border border-stone-300 bg-white px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Ottelun sopiminen</p>
                {activePairCanChooseCourse && !hasTimestampValue(activePair.started) ? (
                  <button
                    type="button"
                    onClick={() => setShowPairCoursePicker((previousValue) => !previousValue)}
                    className="rounded-full border border-cyan-300 bg-cyan-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-cyan-700 transition hover:bg-cyan-100"
                  >
                    Valitse rata
                  </button>
                ) : null}
              </div>

              <div className="mt-3 space-y-3 text-sm text-slate-700">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Pelattava rata</p>
                  <p className="mt-1 text-xs text-slate-500">{activePairCourseChooserLabel}</p>
                  {activePairResolvedCourse ? (
                    <p className="mt-1 font-semibold text-slate-900">{formatCourseLabel(activePairResolvedCourse)}</p>
                  ) : activePairCourseSelection ? (
                    <>
                      <p className="mt-1 font-semibold text-slate-900">{formatCourseLabel(activePairCourseSelection.primaryCourse)}</p>
                      {activePairCourseSelection.requiresApproval ? (
                        <p className="mt-1 text-xs text-slate-500">
                          Odottaa hyväksyntää. Vararata: {formatCourseLabel(activePairCourseSelection.backupCourse)}
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <p className="mt-1 text-sm text-slate-500">Rataa ei ole vielä valittu.</p>
                  )}

                  {activePairNeedsCourseApproval && Number(activePairCourseSelection?.selectedByUserID ?? 0) !== Number(currentUserID) ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => handleRespondPairCourse(true)}
                        disabled={pairPlanningSubmitting !== ''}
                        className="rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-50"
                      >
                        Hyväksy rata
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRespondPairCourse(false)}
                        disabled={pairPlanningSubmitting !== ''}
                        className="rounded-full border border-rose-300 bg-rose-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-rose-700 transition hover:bg-rose-100 disabled:opacity-50"
                      >
                        Hylkää rata
                      </button>
                    </div>
                  ) : null}
                </div>

                {showPairCoursePicker && activePairCanChooseCourse ? (
                  <div className="rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-3">
                    <div className="grid gap-3 md:grid-cols-2">
                      <label className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
                        Rata
                        <select
                          value={pairCourseForm.primaryCourseID}
                          onChange={(event) => setPairCourseForm((previousForm) => ({
                            ...previousForm,
                            primaryCourseID: event.target.value,
                            backupCourseID: '',
                          }))}
                          className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                        >
                          <option value="">Valitse rata</option>
                          {pairCourseOptions.map((course) => (
                            <option key={course.courseID} value={course.courseID}>{formatCourseLabel(course)}</option>
                          ))}
                        </select>
                      </label>

                      {selectedPairCourseRequiresBackup ? (
                        <label className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
                          Vararata kisasta
                          <select
                            value={pairCourseForm.backupCourseID}
                            onChange={(event) => setPairCourseForm((previousForm) => ({ ...previousForm, backupCourseID: event.target.value }))}
                            className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                          >
                            <option value="">Valitse vararata</option>
                            {publicCourses.map((course) => (
                              <option key={course.courseID} value={course.courseID}>{formatCourseLabel(course)}</option>
                            ))}
                          </select>
                        </label>
                      ) : null}
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={handleSubmitPairCourse}
                        disabled={pairPlanningSubmitting !== ''}
                        className="rounded-full border border-cyan-300 bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-cyan-700 transition hover:bg-cyan-100 disabled:opacity-50"
                      >
                        {pairPlanningSubmitting === 'course' ? 'Tallennetaan...' : 'Tallenna rata'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setPairCourseDialogForm(createEmptyCourseForm());
                          setPairCourseDialogOpen(true);
                        }}
                        className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-slate-700 transition hover:bg-slate-100"
                      >
                        Lisää uusi rata
                      </button>
                    </div>
                  </div>
                ) : null}

                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Peliaika</p>
                  {activePairAcceptedTimeProposal ? (
                    <p className="mt-1 font-semibold text-slate-900">{formatDateTime(activePairAcceptedTimeProposal.scheduledStart)}</p>
                  ) : (
                    <p className="mt-1 text-sm text-slate-500">Peliaikaa ei ole vielä hyväksytty.</p>
                  )}
                  <p className="mt-1 text-xs text-slate-500">Voit ehdottaa useita peliaikoja. Vastustaja voi hyväksyä niistä yhden. Peliajan voi sopia jo ennen kierroksen alkua, mutta sen on oltava viimeistään seuraavan kierroksen päättymiseen mennessä.</p>

                  {!hasTimestampValue(activePair?.started) ? (
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-start">
                      <div className="min-w-0 flex-1">
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
                          <DateTimePicker
                            label="Ehdotettu peliaika"
                            ampm={false}
                            format="dd.MM.yyyy HH:mm"
                            value={toDateTimePickerValue(pairProposedTime)}
                            onChange={(newValue) => setPairProposedTime(toDateTimePickerPayloadValue(newValue))}
                            minDateTime={new Date()}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: 'small',
                                className: 'bg-white',
                              },
                              field: {
                                clearable: true,
                              },
                            }}
                          />
                        </LocalizationProvider>
                      </div>
                      <button
                        type="button"
                        onClick={handleProposePairTime}
                        disabled={pairPlanningSubmitting !== ''}
                        className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-slate-700 transition hover:bg-slate-100 disabled:opacity-50 sm:mt-1"
                      >
                        {pairPlanningSubmitting === 'time' ? 'Lähetetään...' : 'Ehdota aikaa'}
                      </button>
                    </div>
                  ) : null}

                  {Array.isArray(activePair?.timeProposals) && activePair.timeProposals.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      {activePair.timeProposals
                        .filter((proposal) => proposal && proposal.status !== 'superseded')
                        .sort((left, right) => String(right?.scheduledStart || '').localeCompare(String(left?.scheduledStart || '')))
                        .map((proposal) => {
                          const isOwnProposal = Number(proposal?.proposedByUserID ?? 0) === Number(currentUserID);
                          const isPendingProposal = proposal?.status === 'pending';

                          return (
                            <div key={proposal.proposalID} className="rounded-xl border border-white bg-white px-3 py-2">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div>
                                  <p className="font-semibold text-slate-900">{formatDateTime(proposal.scheduledStart)}</p>
                                  <p className="text-xs text-slate-500">
                                    {isOwnProposal ? 'Sinun ehdotuksesi' : 'Vastustajan ehdotus'}
                                  </p>
                                </div>
                                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">
                                  {proposal.status === 'accepted' ? 'Hyväksytty' : proposal.status === 'rejected' ? 'Hylätty' : 'Odottaa'}
                                </span>
                              </div>

                              {isPendingProposal && !isOwnProposal ? (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleRespondPairTime(proposal.proposalID, true)}
                                    disabled={pairPlanningSubmitting !== ''}
                                    className="rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-50"
                                  >
                                    Hyväksy aika
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRespondPairTime(proposal.proposalID, false)}
                                    disabled={pairPlanningSubmitting !== ''}
                                    className="rounded-full border border-rose-300 bg-rose-50 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-rose-700 transition hover:bg-rose-100 disabled:opacity-50"
                                  >
                                    Hylkää aika
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                    </div>
                  ) : null}
                </div>

                {activePair?.isReadyForStart ? (
                  activePair?.canStartNow ? null : (
                    <p className="text-xs font-medium text-slate-500">Aloita aktivoituu {formatDateTime(activePair?.canStartAt)}.</p>
                  )
                ) : (
                  <p className="text-xs font-medium text-slate-500">Aloita-nappi aktivoituu vasta kun rata on hyväksytty ja peliaika on sovittu.</p>
                )}
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-col gap-2 sm:items-end">
          {pairsLoaded && nextOpponentInfo?.roundLabel && nextOpponentInfo?.status !== 'finished' ? (
            <span className="rounded-full bg-stone-200 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-700">
              {nextOpponentInfo.roundLabel}
            </span>
          ) : null}
          {shouldShowWaitingForOpponentConfirmation ? (
            <p className="max-w-[12rem] text-[11px] font-semibold text-slate-600 sm:text-right">
              Odottaa vastustajan vahvistusta
            </p>
          ) : null}
          {shouldShowScoringButton ? (
            <button
              type="button"
              onClick={handleStartScoring}
              className="rounded-full border border-stone-400 bg-white px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-slate-800 transition hover:bg-stone-100"
            >
              {scoringButtonLabel}
            </button>
          ) : null}
          {pairsLoaded && (nextOpponentInfo?.status === 'active' || nextOpponentInfo?.status === 'pending') ? (
            <button
              type="button"
              onClick={handleOpenSurrenderModal}
              className="rounded-full border border-rose-300 bg-rose-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-rose-700 transition hover:bg-rose-100"
            >
              Luovuta
            </button>
          ) : null}
        </div>
      </div>
    </div>
  ) : null;

  /* ── COMPACT VIEW ── */
  if (!isExpanded) {
    return (
      <>
        <div className="w-full rounded-[20px] border border-white/10 bg-white/95 px-4 py-3 text-left shadow-[0_8px_30px_-12px_rgba(15,23,42,0.3)] backdrop-blur transition-all hover:bg-white hover:shadow-[0_12px_40px_-12px_rgba(15,23,42,0.4)]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-base font-bold text-slate-900">{competitionName}</p>
                {isRegistered && isRegistrationOpen ? (
                  <button
                    type="button"
                    onClick={handleEditModalOpen}
                    title={registrationIndicatorTitle}
                    aria-label={registrationIndicatorTitle}
                    className={`${registrationIndicatorClassName} transition-transform hover:scale-105`}
                  >
                    R
                  </button>
                ) : (
                  <span
                    title={registrationIndicatorTitle}
                    aria-label={registrationIndicatorTitle}
                    className={registrationIndicatorClassName}
                  >
                    R
                  </span>
                )}
              </div>

              {(compactClassPills.length > 0 || missingRegisteredClassPill) && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {compactClassPills}
                  {missingRegisteredClassPill}
                </div>
              )}

              <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                <p className="text-sm font-semibold text-slate-800">
                  Alkaa: {formatDate(startDate)}{' '}
                  <span className="ml-3">Ilmoittautuminen päättyy: {formatDateTime(registrationEndDate)}</span>
                </p>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={handleCopyCompetitionLink}
                aria-label="Kopioi kilpailun linkki"
                title="Kopioi kilpailun linkki"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 transition hover:bg-slate-100"
              >
                <FiCopy className="text-base" />
              </button>
              <button
                type="button"
                onClick={onExpand}
                aria-label="Avaa kilpailun tiedot"
                title="Avaa kilpailun tiedot"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-700 transition hover:bg-slate-100"
              >
                <FiChevronDown className="text-lg" />
              </button>
            </div>
          </div>

          {/* Edit modal rendered even in compact mode */}
          {showEditModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={(e) => e.stopPropagation()}>
              <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <h3 className="mb-1 text-lg font-bold text-slate-900">Rekisteröitymisesi</h3>
                <p className="mb-4 text-sm text-slate-500">{competitionName}</p>
                {classes.length > 0 ? (
                  <>
                    <p className="mb-2 text-sm font-semibold text-slate-700">Valittu luokka</p>
                    <div className="space-y-2">
                      {eligibleClasses.map((cls) => {
                        const age = ageLabel(cls.ageOperator, cls.ageYear);
                        return (
                          <label key={cls.classID} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors ${selectedClassID === Number(cls.classID) ? 'border-cyan-500 bg-cyan-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                            <input type="radio" name="editCompetitionClass" value={cls.classID} checked={selectedClassID === Number(cls.classID)} onChange={() => setSelectedClassID(Number(cls.classID))} className="accent-cyan-600" />
                            <span className="font-medium text-slate-800">{cls.name}</span>
                            <span className="ml-auto text-xs text-slate-500">{genderLabel(cls.gender)}{age ? ` · ${age}` : ''}</span>
                          </label>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-600">Kilpailussa ei ole luokkia.</p>
                )}
                <div className="mt-6 flex items-center justify-between gap-3">
                  <button type="button" onClick={handleEditModalCancel} className="rounded-full border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-100">Poista rekisteröinti</button>
                  <div className="flex gap-2">
                    <button type="button" onClick={(e) => { e.stopPropagation(); setShowEditModal(false); }} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-100">Sulje</button>
                    {classes.length > 0 && <button type="button" onClick={handleEditModalUpdate} disabled={selectedClassID === null} className="rounded-full bg-cyan-600 px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-40">Päivitä</button>}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {isOpponentModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={handleCloseOpponentModal}>
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-amber-700">Seuraava vastustajasi</p>
                  <h3 className="mt-2 text-xl font-bold text-slate-900">{opponentContact?.name || nextOpponentInfo?.value || 'Yhteystiedot'}</h3>
                </div>
                <button type="button" onClick={handleCloseOpponentModal} className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700 hover:bg-slate-200">
                  Sulje
                </button>
              </div>

              {opponentContactLoading ? (
                <p className="mt-5 text-sm text-slate-500">Haetaan yhteystietoja...</p>
              ) : opponentContactError ? (
                <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{opponentContactError}</div>
              ) : (
                <div className="mt-5 space-y-3">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Puhelinnumero</p>
                    {opponentContact?.phone ? (
                      <a
                        href={`tel:${opponentContact.phone}`}
                        className="mt-1 inline-block text-sm font-semibold text-cyan-700 underline decoration-cyan-400 underline-offset-2 hover:text-cyan-900"
                      >
                        {opponentContact.phone}
                      </a>
                    ) : (
                      <p className="mt-1 text-sm font-semibold text-slate-900">Ei ilmoitettu</p>
                    )}
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Paikkakunta</p>
                    <p className="mt-1 text-sm font-semibold text-slate-900">{opponentContact?.municipality || 'Ei ilmoitettu'}</p>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Sähköposti</p>
                    {opponentContact?.email ? (
                      <a
                        href={`mailto:${opponentContact.email}`}
                        className="mt-1 inline-block text-sm font-semibold text-cyan-700 underline decoration-cyan-400 underline-offset-2 hover:text-cyan-900"
                      >
                        {opponentContact.email}
                      </a>
                    ) : (
                      <p className="mt-1 text-sm font-semibold text-slate-900">Ei ilmoitettu</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {showSurrenderModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={handleCloseSurrenderModal}>
            <div className="w-full max-w-lg rounded-[32px] border border-white/10 bg-slate-950 p-6 shadow-2xl shadow-black/50" onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-white/10 pb-4">
                <p className="text-sm font-semibold uppercase tracking-[0.35em] text-rose-300">Ottelun luovutus</p>
                <h2 className="mt-2 text-2xl font-black text-white">Haluatko varmasti luovuttaa?</h2>
                <p className="mt-3 text-sm leading-6 text-slate-300">
                  Luovutus päättää kilpailun omalta osaltasi. Kilpailun pelisi merkitään pelatuiksi ja vastustaja menee jatkoon.
                </p>
                <p className="mt-3 rounded-2xl border border-rose-400/30 bg-rose-400/10 px-4 py-3 text-sm font-semibold text-rose-100">
                  Jos vastustaja ei ole vielä ratkennut, hän menee suoraan jatkoon heti kun pari selviää.
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCloseSurrenderModal}
                  disabled={surrenderSubmitting}
                  className="rounded-full border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Peruuta
                </button>
                <button
                  type="button"
                  onClick={handleConfirmSurrender}
                  disabled={surrenderSubmitting}
                  className="rounded-full bg-rose-500 px-5 py-2 text-sm font-bold text-white transition hover:bg-rose-400 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {surrenderSubmitting ? 'Tallennetaan...' : 'Luovuta ottelu'}
                </button>
              </div>
            </div>
          </div>
        )}

        <CourseDialog
          open={pairCourseDialogOpen}
          courseForm={pairCourseDialogForm}
          saving={pairCourseDialogSaving}
          onClose={() => {
            if (!pairCourseDialogSaving) {
              setPairCourseDialogOpen(false);
            }
          }}
          onSubmit={handleSavePairCourseDialog}
          onFieldChange={handlePairCourseDialogFieldChange}
        />
      </>
    );
  }

  /* ── EXPANDED VIEW ── */
  return (
    <>
    <div className="rounded-[28px] border border-white/10 bg-white/95 p-4 shadow-[0_20px_70px_-30px_rgba(15,23,42,0.45)] backdrop-blur">
      <div className="mb-4 flex items-start justify-between gap-3 border-b border-slate-200 pb-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-bold text-slate-900 md:text-2xl">{competitionName}</h2>
            {isRegistered && isRegistrationOpen ? (
              <button
                type="button"
                onClick={handleEditModalOpen}
                title={registrationIndicatorTitle}
                aria-label={registrationIndicatorTitle}
                className={`${registrationIndicatorClassName} transition-transform hover:scale-105`}
              >
                R
              </button>
            ) : (
              <span
                title={registrationIndicatorTitle}
                aria-label={registrationIndicatorTitle}
                className={registrationIndicatorClassName}
              >
                R
              </span>
            )}
          </div>
          {(classes.length > 0 || missingRegisteredClassPill) && (
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {classPills}
              {missingRegisteredClassPill}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={handleCopyCompetitionLink}
            aria-label="Kopioi kilpailun linkki"
            title="Kopioi kilpailun linkki"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 text-slate-700 transition hover:bg-slate-100"
          >
            <FiCopy className="text-base" />
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sulje kilpailun tiedot"
            title="Sulje kilpailun tiedot"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 text-slate-700 transition hover:bg-slate-100"
          >
            <FiChevronUp className="text-lg" />
          </button>
        </div>
      </div>

      <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="mb-2">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Toiminnot</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {!isRegistrationOpen ? (
              <button
                type="button"
                onClick={handleOpenResults}
                aria-label="Cup-kaavio"
                title="Cup-kaavio"
                className="inline-flex h-10 items-center justify-center rounded-full border border-cyan-200 bg-cyan-50 px-4 text-sm font-bold text-cyan-700 transition hover:bg-cyan-100"
              >
                Cup-kaavio
              </button>
            ) : null}
            {!isRegistrationOpen && isAdminUser && !isCompetitionStarted ? (
              <button
                type="button"
                onClick={handleCreatePairs}
                disabled={pairsLoading}
                className="inline-flex h-10 items-center justify-center rounded-full bg-slate-700 px-4 text-sm font-bold text-white transition hover:bg-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {pairsLoading ? 'Luodaan...' : 'Luo parit'}
              </button>
            ) : null}
            {!isRegistrationOpen && isAdminUser && pairsLoaded && pairs.length > 0 && !isCompetitionStarted ? (
              <button
                type="button"
                onClick={handleOpenStartCompetitionModal}
                disabled={startCompetitionSubmitting}
                className="inline-flex h-10 items-center justify-center rounded-full bg-emerald-600 px-4 text-sm font-bold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {startCompetitionSubmitting ? 'Aloitetaan...' : 'Aloita kisa'}
              </button>
            ) : null}
            {isAuthenticated && isAuthenticated() && isRegistrationOpen && !isRegistered ? (
              <button
                type="button"
                onClick={handleRegisterClick}
                className="inline-flex h-10 items-center justify-center rounded-full bg-cyan-600 px-5 text-sm font-bold text-white transition-colors hover:bg-cyan-500"
              >
                Rekisteröidy
              </button>
            ) : null}
            {isAuthenticated && isAuthenticated() && isRegistrationOpen && isRegistered ? (
              <button
                type="button"
                onClick={handleEditModalOpen}
                className="inline-flex h-10 items-center justify-center rounded-full border border-red-200 bg-red-50 px-5 text-sm font-bold text-red-700 transition-colors hover:bg-red-100"
              >
                Peruuta rekisteröityminen
              </button>
            ) : null}
            {canEditCompetition && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); navigate(`/muokkaa_kisa/${competition.competitionID}`); }}
                className="inline-flex h-10 w-fit items-center justify-center rounded-full bg-slate-100 px-3 text-xs font-bold uppercase tracking-[0.25em] text-slate-600 transition-colors hover:bg-slate-200"
              >
                Muokkaa
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {(nextOpponentBlock || finishedCompetitionBlock) ? renderSection(
          'status',
          'Seuraava vastustajasi',
          <div className="space-y-3">
            {nextOpponentBlock}
            {finishedCompetitionBlock}
          </div>,
          { subtitle: 'Kilpailun tämänhetkinen eteneminen.' }
        ) : null}

        {renderSection(
          'info',
          'Säännöt ja lisätiedot',
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => handleOpenHelpTopic('competitionGuidelines')}
                className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
              >
                Tämän sivuston ohjeet
              </button>
              <a
                href="https://frisbeegolfliitto.fi/frisbeegolfin-virallinen-saantokirja-ja-kilpailuopas/#reikapelin"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center rounded-full border border-cyan-300 bg-cyan-50 px-4 py-2 text-sm font-semibold text-cyan-700 transition hover:bg-cyan-100"
              >
                Reikäpelin ohjeet
              </a>
            </div>
            <p className="whitespace-pre-wrap text-sm leading-6 text-slate-700">{info || 'Lisätietoja ei ole annettu.'}</p>
          </div>
        )}

        {renderSection(
          'organizer',
          'Kilpailun järjestäjä',
          <div>
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-sm leading-6 text-slate-700">
                  Kilpailuun liittyvät kysymykset välitetään kilpailun järjestäjälle.
                </p>
              </div>

              {isUserAuthenticated ? (
                <button
                  type="button"
                  onClick={handleOpenOrganizerMessageModal}
                  disabled={competitionOrganizerUsers.length === 0}
                  className="rounded-full border border-cyan-300 bg-white px-4 py-2 text-sm font-semibold text-cyan-700 transition hover:bg-cyan-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400"
                >
                  Lähetä viesti järjestäjälle
                </button>
              ) : null}
            </div>

            {competition?.organizerID ? (
              <div className="mt-4 rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-4">
                  <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-100">
                    {organizerLogoUrl ? (
                      <img
                        src={organizerLogoUrl}
                        alt={organizerName}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <span className="text-2xl font-black uppercase text-slate-500">
                        {organizerName.slice(0, 1) || 'J'}
                      </span>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Järjestäjä</p>
                    <div className="mt-2 flex items-start justify-between gap-3">
                      <p className="min-w-0 flex-1 text-xl font-bold text-slate-900">{organizerName}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-3 text-sm text-slate-500">
                Tälle kilpailulle ei ole vielä määritetty järjestäjää.
              </div>
            )}

            {!isUserAuthenticated ? (
              <p className="mt-3 text-sm text-slate-500">Kirjaudu sisään, jos haluat lähettää viestin kilpailun järjestäjälle.</p>
            ) : null}
          </div>
        )}

        {renderSection(
          'schedule',
          'Aikataulu',
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Kierroksen kesto</p>
              <p className="mt-2 text-lg font-semibold text-slate-900">{roundtime} päivää</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Rekisteröinti päättyy</p>
              <p className="mt-2 text-lg font-semibold text-slate-900">{formatDateTime(registrationEndDate)}</p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Kisa alkaa</p>
              <p className="mt-2 text-lg font-semibold text-slate-900">{formatDate(startDate)}</p>
            </div>
          </div>
        )}

        {renderSection(
          'courses',
          'Radat',
          publicCourses.length ? (
            <div className="flex flex-wrap gap-3">
              {publicCourses.map((course) => {
                const locationParts = [course.municipality, course.region].filter(Boolean);

                return (
                  <div key={course.courseID || course.name} className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
                    <p className="font-semibold text-slate-900">{course.name}</p>
                    {locationParts.length ? (
                      <p className="mt-1 text-xs text-slate-500">{locationParts.join(', ')}</p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-3 text-sm text-slate-500">
              Tälle kilpailulle ei ole lisätty ratoja.
            </div>
          ),
          { subtitle: publicCourses.length ? `${publicCourses.length} rataa` : null }
        )}

        {renderSection(
          'payments',
          'Maksutiedot',
          <div>
            <p className="text-sm leading-6 text-slate-700">
              Kisamaksun tulee olla maksettuna rekisteröinnin päättyessä tai välittömästi sen jälkeen, jos rekisteröityy viime minuuteilla.
              Kun ensimmäisen kierroksen parit luodaan, maksamattomat poistetaan automaattisesti ilman erillistä ilmoitusta.
            </p>

            <div className="mt-4 grid gap-4 lg:grid-cols-2">
              {enableBankPayment ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 shadow-sm">
                  <div className="border-b border-slate-200 pb-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Tilimaksu</p>
                    <p className="mt-1 text-sm text-slate-700">Maksu pankkitilille kilpailun maksutiedoilla.</p>
                  </div>
                  <div className="mt-3 grid gap-3">
                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
                      {renderCopyableField('Tilinumero', bankAccount, 'Tilinumero kopioitu.')}
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
                      {renderCopyableField('Tilisiirron saaja', bankRecipientName || organizerName, 'Tilisiirron saaja kopioitu.')}
                    </div>
                    {usePersonalReferenceNumber ? (
                      <div className="rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm text-slate-700 shadow-sm">
                        {renderCopyableField(
                          'Henkilökohtainen viitenumero',
                          personalPaymentReferenceNumber,
                          'Viitenumero kopioitu.',
                          { accent: 'cyan' }
                        )}
                        {!personalPaymentReferenceNumber ? (
                          <p className="mt-1 font-semibold text-slate-900">
                            {isRegistered ? 'Luodaan...' : 'Luodaan rekisteröitymisen jälkeen'}
                          </p>
                        ) : null}
                        <p className="mt-1 text-xs text-slate-500">Samaa viitenumeroa käytetään sekä tilisiirrossa että MobilePayssä.</p>
                      </div>
                    ) : null}
                    {!usePersonalReferenceNumber && bankPaymentMessage ? (
                      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
                        {renderCopyableField('Tilisiirron viesti', bankReferenceOrMessage, 'Maksuviesti kopioitu.')}
                      </div>
                    ) : null}
                    {!usePersonalReferenceNumber && bankPaymentBarcode ? (
                      <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
                        {renderCopyableField('Yhteinen viitenumero', bankPaymentBarcode, 'Viitenumero kopioitu.', { breakAll: true })}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {enableMobilepayPayment ? (
                <div className="rounded-2xl border border-slate-200 bg-emerald-50/70 p-4 shadow-sm">
                  <div className="border-b border-emerald-200 pb-3">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">MobilePay</p>
                    <p className="mt-1 text-sm text-slate-700">Nopea maksu suoraan MobilePayhin.</p>
                  </div>
                  <div className="mt-3 grid gap-3">
                    <div className="rounded-xl border border-emerald-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
                      {renderCopyableField('MobilePay', mobilepayNumber, 'MobilePay-numero kopioitu.')}
                    </div>
                    {usePersonalReferenceNumber ? (
                      <div className="rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm text-slate-700 shadow-sm">
                        {renderCopyableField(
                          'Henkilökohtainen viitenumero',
                          personalPaymentReferenceNumber,
                          'Viitenumero kopioitu.',
                          { accent: 'cyan' }
                        )}
                        {!personalPaymentReferenceNumber ? (
                          <p className="mt-1 font-semibold text-slate-900">
                            {isRegistered ? 'Luodaan...' : 'Luodaan rekisteröitymisen jälkeen'}
                          </p>
                        ) : null}
                        <p className="mt-1 text-xs text-slate-500">Samaa viitenumeroa käytetään sekä tilisiirrossa että MobilePayssä.</p>
                      </div>
                    ) : null}
                    {!usePersonalReferenceNumber && mobilepayMessage ? (
                      <div className="rounded-xl border border-emerald-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
                        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">MobilePay-viesti</p>
                        <p className="mt-1 font-semibold text-slate-900">{mobilepayMessage}</p>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="mt-4 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                {useClassSpecificEntryFee ? 'Luokkakohtaiset maksut' : 'Osallistumismaksu'}
              </p>

              {!useClassSpecificEntryFee ? (
                <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
                  <p className="font-semibold text-slate-900">{sharedEntryFee !== null ? formatMoney(sharedEntryFee) : 'Ei asetettu'}</p>
                  <p className="mt-1 text-xs text-slate-500">Sama maksu koskee kaikkia luokkia.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {publicClassSummaries.length ? publicClassSummaries.map((summary) => (
                    <div key={summary.classID ?? summary.className} className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="font-semibold text-slate-900">{summary.className}</p>
                        <p className="font-bold text-slate-900">{summary.entryFee !== null ? formatMoney(summary.entryFee) : 'Ei asetettu'}</p>
                      </div>
                      {isRegistrationOpen ? (
                        <p className="mt-1 text-xs text-slate-500">Nyt rekisteröityneitä: {summary.playerCount}</p>
                      ) : null}
                    </div>
                  )) : (
                    <p className="text-sm text-slate-500">Luokkakohtaisia maksuja ei ole asetettu.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {renderSection(
          'prizes',
          'Palkinnot',
          <div>
            <p className="text-sm font-semibold leading-6 text-slate-700">
              Palkintotyyppi: {normalizedPrizeSettings?.prizeType === 'cash'
                ? 'Käteinen'
                : normalizedPrizeSettings?.prizeType === 'gift_card'
                  ? 'Lahjakortti'
                  : normalizedPrizeSettings?.prizeType === 'trophies'
                    ? 'Pokaalit'
                    : normalizedPrizeSettings?.prizeType === 'product_prizes'
                      ? 'Tuotepalkinnot'
                      : 'Palkinnot'}
            </p>
            {isPayoutPrizeType(normalizedPrizeSettings?.prizeType) ? (
              <div className="space-y-1">
                <p className="text-sm font-semibold leading-6 text-slate-700">
                  Payout: {normalizedPrizeSettings?.payoutAmount !== '' ? `${normalizedPrizeSettings.payoutAmount} %` : 'Ei asetettu'}
                </p>
                <p className="text-sm leading-6 text-slate-700">Palkintojako määräytyy luokkakohtaisen pelaajamäärän mukaan.</p>
              </div>
            ) : null}

            {isRegistrationOpen && prizeRuleSummaries.length ? (
              <div className="mt-4 rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm text-slate-700">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-700">Nykyiset palkintosäännöt</p>
                <div className="mt-2 space-y-1">
                  {prizeRuleSummaries.map((ruleSummary) => (
                    <p key={ruleSummary.minPlayers}>
                      Vähintään {ruleSummary.minPlayers} pelaajaa: palkitaan {ruleSummary.winnerCount} pelaajaa ({ruleSummary.placements.join(', ')})
                    </p>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="mt-4 space-y-3">
              {publicPrizeStatusByClass.length ? publicPrizeStatusByClass.map((summary) => (
                <div key={summary.classID ?? summary.className} className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-semibold text-slate-900">{summary.className}</p>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{summary.playerCount} pelaajaa</p>
                  </div>

                  {!summary.playerCount ? (
                    <p className="mt-2 text-sm text-slate-500">Ei rekisteröityneitä vielä.</p>
                  ) : isPayoutPrizeType(normalizedPrizeSettings?.prizeType) ? (
                    summary.applicableRule && summary.totalEntryFees !== null ? (
                      <>
                        <div className="mt-2 grid gap-2 sm:grid-cols-2">
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Osallistumismaksut yhteensä</p>
                            <p className="mt-1 font-semibold text-slate-900">{formatMoney(summary.totalEntryFees)}</p>
                          </div>
                          <div>
                            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Palkintopotti</p>
                            <p className="mt-1 font-semibold text-slate-900">{formatMoney(summary.payoutPool)}</p>
                          </div>
                        </div>
                        <div className="mt-3 space-y-1.5">
                          {summary.previewRows.map((row) => (
                            <div key={`${summary.classID ?? summary.className}-${row.key}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2">
                              <div>
                                <p className="font-semibold text-slate-900">{row.placementLabel}</p>
                                <p className="text-xs text-slate-500">{row.percentage} % potista</p>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-slate-900">{formatMoney(row.totalAmount)}</p>
                                {row.playerCount > 1 ? (
                                  <p className="text-xs text-slate-500">/ pelaaja {formatMoney(row.perPlayerAmount)}</p>
                                ) : null}
                              </div>
                            </div>
                          ))}
                        </div>
                        {isRegistrationOpen ? (
                          <p className="mt-2 text-xs text-slate-500">Tilanne päivittyy rekisteröintimäärän mukaan rekisteröinnin loppuun asti.</p>
                        ) : null}
                      </>
                    ) : (
                      <p className="mt-2 text-sm text-slate-500">Nykyinen pelaajamäärä ei vielä täytä palkintosäännöissä asetettua minimirajaa.</p>
                    )
                  ) : (
                    <p className="mt-2 text-sm text-slate-500">
                      {prizeRuleSummaries.length
                        ? `Nykyisellä pelaajamäärällä palkitaan ${summary.applicableRule?.winnerCount || 0} pelaajaa.`
                        : 'Tälle palkintotyypille ei ole rahallista jakolaskentaa.'}
                    </p>
                  )}
                </div>
              )) : (
                <p className="text-sm text-slate-500">Palkintotilannetta ei voida näyttää ilman luokkia.</p>
              )}
            </div>
          </div>
        )}

        {renderSection(
          'participants',
          'Rekisteröityneet pelaajat',
          <div>
            <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
              <div>
                {participants !== null ? (
                  <p className="text-sm text-slate-500">Näytetään {totalVisibleParticipants} pelaajaa.</p>
                ) : null}
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:min-w-[32rem]">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                    {canManagePayments ? 'Haku nimellä tai viitenumerolla' : 'Haku nimellä'}
                  </span>
                  <input
                    type="search"
                    value={participantSearch}
                    onChange={(event) => setParticipantSearch(event.target.value)}
                    placeholder={canManagePayments ? 'Etsi pelaajaa nimellä tai viitenumerolla' : 'Etsi pelaajaa nimellä'}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm outline-none transition focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100"
                  />
                </label>

                {canManagePayments ? (
                  <label className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Maksutila</span>
                    <select
                      value={participantPaymentFilter}
                      onChange={(event) => setParticipantPaymentFilter(event.target.value)}
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm outline-none transition focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100"
                    >
                      <option value="all">Kaikki</option>
                      <option value="paid">Vain maksetut</option>
                      <option value="unpaid">Vain maksamattomat</option>
                    </select>
                  </label>
                ) : null}
              </div>
            </div>
            {participants === null ? (
              <p className="text-sm text-slate-400">Ladataan...</p>
            ) : participants.length === 0 ? (
              <p className="text-sm text-slate-500">Ei rekisteröityneitä pelaajia.</p>
            ) : visibleParticipantGroups.length === 0 ? (
              <p className="text-sm text-slate-500">Hakuehdoilla ei löytynyt pelaajia.</p>
            ) : (
              <div className="space-y-4">
                {visibleParticipantGroups.map((group) => (
                  <div key={group.classID ?? 'noclass'}>
                    <p className="mb-1.5 text-xs font-bold uppercase tracking-[0.15em] text-slate-600">
                      {group.className || (group.classID !== null && group.classID !== undefined ? `Luokka #${group.classID}` : 'Ei luokkaa')}
                    </p>
                    <ul className="space-y-1">
                      {group.players.map((player, i) => {
                        const participant = typeof player === 'object' && player !== null
                          ? player
                          : { userID: i, name: String(player || ''), isPaid: false, paidAt: null };
                        const participantUserID = Number(participant.userID ?? 0);
                        const isCurrentUserParticipant = currentUserID !== null && participantUserID === currentUserID;
                        const isUpdatingPayment = paymentUpdatingUserID !== null && paymentUpdatingUserID === participantUserID;
                        const participantReferenceNumber = String(participant.paymentReferenceNumber || '').trim();

                        return (
                          <li
                            key={participantUserID || `${group.classID ?? 'noclass'}-${i}`}
                            className={isCurrentUserParticipant
                              ? 'flex flex-wrap items-center justify-between gap-3 rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2 text-sm text-slate-700'
                              : 'flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700'}
                          >
                            <div className="min-w-0">
                              <div className="flex min-w-0 items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className={isCurrentUserParticipant ? 'w-3.5 h-3.5 shrink-0 text-cyan-500' : 'w-3.5 h-3.5 shrink-0 text-slate-400'}>
                                  <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM12.735 14c.618 0 1.093-.561.872-1.139a6.002 6.002 0 0 0-11.215 0c-.22.578.254 1.139.872 1.139h9.47Z" />
                                </svg>
                                <span className={isCurrentUserParticipant ? 'font-semibold text-cyan-900' : 'font-medium text-slate-800'}>{participant.name}</span>
                              </div>
                              {participant.municipality ? (
                                <p className="ml-5 mt-1 text-xs font-medium text-slate-500">
                                  Paikkakunta: <span className="font-semibold text-slate-700">{participant.municipality}</span>
                                </p>
                              ) : null}
                              {participantReferenceNumber ? (
                                <p className="ml-5 mt-1 text-xs font-medium text-slate-500">
                                  Viitenumero: <span className="font-semibold text-slate-700">{participantReferenceNumber}</span>
                                </p>
                              ) : null}
                            </div>

                            {isAuthenticated && isAuthenticated() ? (
                              <div className="flex flex-wrap items-center justify-end gap-2">
                                {participant.isPaid ? (
                                  <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold uppercase tracking-[0.14em] text-emerald-700">
                                    Maksettu
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold uppercase tracking-[0.14em] text-amber-700">
                                    Ei maksettu
                                  </span>
                                )}
                                {canManagePayments ? (
                                  <button
                                    type="button"
                                    onClick={() => handleTogglePaymentStatus(participant)}
                                    disabled={isUpdatingPayment}
                                    className={participant.isPaid
                                      ? 'rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50'
                                      : 'rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50'}
                                  >
                                    {isUpdatingPayment ? 'Tallennetaan...' : (participant.isPaid ? 'Poista merkintä' : 'Merkitse maksetuksi')}
                                  </button>
                                ) : null}
                              </div>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>,
          { subtitle: participants !== null ? `Näytetään ${totalVisibleParticipants} pelaajaa.` : null }
        )}
      </div>

    </div>

    {/* Class selection modal */}
    {showClassModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
          <h3 className="mb-4 text-lg font-bold text-slate-900">Valitse luokka</h3>
          {eligibleClasses.length === 0 ? (
            <p className="text-sm text-slate-600">Sinulle ei löydy sopivia luokkia tässä kilpailussa.</p>
          ) : (
            <div className="space-y-2">
              {eligibleClasses.map((cls) => {
                const age = ageLabel(cls.ageOperator, cls.ageYear);
                return (
                  <label key={cls.classID} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors ${selectedClassID === Number(cls.classID) ? 'border-cyan-500 bg-cyan-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                    <input type="radio" name="competitionClass" value={cls.classID} checked={selectedClassID === Number(cls.classID)} onChange={() => setSelectedClassID(Number(cls.classID))} className="accent-cyan-600" />
                    <span className="font-medium text-slate-800">{cls.name}</span>
                    <span className="ml-auto text-xs text-slate-500">{genderLabel(cls.gender)}{age ? ` · ${age}` : ''}</span>
                  </label>
                );
              })}
            </div>
          )}
          <div className="mt-6 flex justify-end gap-3">
            <button type="button" onClick={() => setShowClassModal(false)} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors">Peruuta</button>
            {eligibleClasses.length > 0 && (
              <button type="button" onClick={handleModalConfirm} disabled={selectedClassID === null} className="rounded-full bg-cyan-600 px-5 py-2 text-sm font-bold text-white hover:bg-cyan-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Rekisteröidy</button>
            )}
          </div>
        </div>
      </div>
    )}

    {showCreatePairsModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={handleCloseCreatePairsModal}>
        <div className="w-full max-w-md rounded-[32px] border border-white/10 bg-slate-950 p-6 shadow-2xl shadow-black/50" onClick={(e) => e.stopPropagation()}>
          <div className="border-b border-white/10 pb-4">
            <p className="text-sm font-semibold uppercase tracking-[0.35em] text-amber-300">Parien luonti</p>
            <h2 className="mt-2 text-2xl font-black text-white">Luodaanko parit kilpailulle?</h2>
            <p className="mt-3 text-sm text-slate-300">
              Kilpailu: <span className="font-semibold text-white">{competitionName}</span>
            </p>
            <p className="mt-2 text-sm text-slate-400">Toimintoa ei voi peruuttaa.</p>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleCloseCreatePairsModal}
              className="rounded-full border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:bg-white/10"
            >
              Peruuta
            </button>
            <button
              type="button"
              onClick={handleConfirmCreatePairs}
              disabled={pairsLoading}
              className="rounded-full bg-amber-500 px-5 py-2 text-sm font-bold text-slate-950 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {pairsLoading ? 'Luodaan...' : 'Luo parit'}
            </button>
          </div>
        </div>
      </div>
    )}

    {showStartCompetitionModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={handleCloseStartCompetitionModal}>
        <div className="w-full max-w-lg rounded-[32px] border border-white/10 bg-slate-950 p-6 shadow-2xl shadow-black/50" onClick={(e) => e.stopPropagation()}>
          <div className="border-b border-white/10 pb-4">
            <p className="text-sm font-semibold uppercase tracking-[0.35em] text-emerald-300">Kilpailun aloitus</p>
            <h2 className="mt-2 text-2xl font-black text-white">Aloitetaanko kilpailu nyt?</h2>
            <p className="mt-3 text-sm leading-6 text-slate-300">
              Tämä merkitsee kilpailun alkaneeksi ja lähettää osallistujille sähköpostin, että ensimmäisen kierroksen parit on arvottu.
            </p>
            <p className="mt-3 rounded-2xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-sm font-semibold text-emerald-100">
              Viestissä ohjataan ehdottamaan aikaa palvelussa, tarkistamaan radan valitsija ja avamaan kilpailu kisalinkistä.
            </p>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleCloseStartCompetitionModal}
              disabled={startCompetitionSubmitting}
              className="rounded-full border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Peruuta
            </button>
            <button
              type="button"
              onClick={handleStartCompetition}
              disabled={startCompetitionSubmitting}
              className="rounded-full bg-emerald-500 px-5 py-2 text-sm font-bold text-white transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {startCompetitionSubmitting ? 'Aloitetaan...' : 'Aloita kisa'}
            </button>
          </div>
        </div>
      </div>
    )}

    {showSurrenderModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={handleCloseSurrenderModal}>
        <div className="w-full max-w-lg rounded-[32px] border border-white/10 bg-slate-950 p-6 shadow-2xl shadow-black/50" onClick={(e) => e.stopPropagation()}>
          <div className="border-b border-white/10 pb-4">
            <p className="text-sm font-semibold uppercase tracking-[0.35em] text-rose-300">Ottelun luovutus</p>
            <h2 className="mt-2 text-2xl font-black text-white">Haluatko varmasti luovuttaa?</h2>
            <p className="mt-3 text-sm leading-6 text-slate-300">
              Luovutus päättää kilpailun omalta osaltasi. Kilpailun pelisi merkitään pelatuiksi ja vastustaja menee jatkoon.
            </p>
            <p className="mt-3 rounded-2xl border border-rose-400/30 bg-rose-400/10 px-4 py-3 text-sm font-semibold text-rose-100">
              Jos vastustaja ei ole vielä ratkennut, hän menee suoraan jatkoon heti kun pari selviää.
            </p>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleCloseSurrenderModal}
              disabled={surrenderSubmitting}
              className="rounded-full border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Peruuta
            </button>
            <button
              type="button"
              onClick={handleConfirmSurrender}
              disabled={surrenderSubmitting}
              className="rounded-full bg-rose-500 px-5 py-2 text-sm font-bold text-white transition hover:bg-rose-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {surrenderSubmitting ? 'Tallennetaan...' : 'Luovuta ottelu'}
            </button>
          </div>
        </div>
      </div>
    )}

    {showOrganizerMessageModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={handleCloseOrganizerMessageModal}>
        <div className="w-full max-w-xl rounded-[32px] border border-white/10 bg-slate-950 p-6 shadow-2xl shadow-black/50" onClick={(e) => e.stopPropagation()}>
          <div className="border-b border-white/10 pb-4">
            <p className="text-sm font-semibold uppercase tracking-[0.35em] text-cyan-300">Yhteys kilpailun järjestäjään</p>
            <h2 className="mt-2 text-2xl font-black text-white">Lähetä viesti</h2>
            <p className="mt-3 text-sm text-slate-300">
              Viesti välitetään kilpailun järjestäjälle kilpailusta <span className="font-semibold text-white">{competitionName}</span>.
            </p>
          </div>

          <div className="mt-5 space-y-4">
            <div>
              <label htmlFor={`organizer-message-subject-${competition?.competitionID}`} className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">
                Aihe
              </label>
              <input
                id={`organizer-message-subject-${competition?.competitionID}`}
                type="text"
                value={organizerMessageSubject}
                onChange={(event) => setOrganizerMessageSubject(event.target.value)}
                placeholder="Esim. aikataulu, maksu tai käytännön kysymys"
                className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-300"
              />
            </div>

            <div>
              <label htmlFor={`organizer-message-body-${competition?.competitionID}`} className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">
                Viesti
              </label>
              <textarea
                id={`organizer-message-body-${competition?.competitionID}`}
                rows={7}
                value={organizerMessageBody}
                onChange={(event) => setOrganizerMessageBody(event.target.value)}
                placeholder="Kirjoita viesti kilpailun järjestäjälle"
                className="w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm leading-6 text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-300"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleCloseOrganizerMessageModal}
              disabled={organizerMessageSending}
              className="rounded-full border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Peruuta
            </button>
            <button
              type="button"
              onClick={handleSendOrganizerMessage}
              disabled={organizerMessageSending}
              className="rounded-full bg-cyan-400 px-5 py-2 text-sm font-bold text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {organizerMessageSending ? 'Lähetetään...' : 'Lähetä viesti'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Edit registration modal */}
    {showEditModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
          <h3 className="mb-1 text-lg font-bold text-slate-900">Rekisteröitymisesi</h3>
          <p className="mb-4 text-sm text-slate-500">{competitionName}</p>
          {classes.length > 0 ? (
            <>
              <p className="mb-2 text-sm font-semibold text-slate-700">Valittu luokka</p>
              <div className="space-y-2">
                {eligibleClasses.map((cls) => {
                  const age = ageLabel(cls.ageOperator, cls.ageYear);
                  return (
                    <label key={cls.classID} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors ${selectedClassID === Number(cls.classID) ? 'border-cyan-500 bg-cyan-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                      <input type="radio" name="editCompetitionClass" value={cls.classID} checked={selectedClassID === Number(cls.classID)} onChange={() => setSelectedClassID(Number(cls.classID))} className="accent-cyan-600" />
                      <span className="font-medium text-slate-800">{cls.name}</span>
                      <span className="ml-auto text-xs text-slate-500">{genderLabel(cls.gender)}{age ? ` · ${age}` : ''}</span>
                    </label>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-600">Kilpailussa ei ole luokkia.</p>
          )}
          <div className="mt-6 flex items-center justify-between gap-3">
            <button type="button" onClick={handleEditModalCancel} className="rounded-full bg-red-50 border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-100 transition-colors">Poista rekisteröinti</button>
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowEditModal(false)} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors">Sulje</button>
              {classes.length > 0 && <button type="button" onClick={handleEditModalUpdate} disabled={selectedClassID === null} className="rounded-full bg-cyan-600 px-5 py-2 text-sm font-bold text-white hover:bg-cyan-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Päivitä</button>}
            </div>
          </div>
        </div>
      </div>
    )}

    {isOpponentModalOpen && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={handleCloseOpponentModal}>
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-amber-700">Seuraava vastustaja</p>
              <h3 className="mt-2 text-xl font-bold text-slate-900">{opponentContact?.name || nextOpponentInfo?.value || 'Yhteystiedot'}</h3>
            </div>
            <button type="button" onClick={handleCloseOpponentModal} className="rounded-full bg-slate-100 px-3 py-1 text-sm font-semibold text-slate-700 hover:bg-slate-200">
              Sulje
            </button>
          </div>

          {opponentContactLoading ? (
            <p className="mt-5 text-sm text-slate-500">Haetaan yhteystietoja...</p>
          ) : opponentContactError ? (
            <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{opponentContactError}</div>
          ) : (
            <div className="mt-5 space-y-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Puhelinnumero</p>
                {opponentContact?.phone ? (
                  <a
                    href={`tel:${opponentContact.phone}`}
                    className="mt-1 inline-block text-sm font-semibold text-cyan-700 underline decoration-cyan-400 underline-offset-2 hover:text-cyan-900"
                  >
                    {opponentContact.phone}
                  </a>
                ) : (
                  <p className="mt-1 text-sm font-semibold text-slate-900">Ei ilmoitettu</p>
                )}
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Paikkakunta</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{opponentContact?.municipality || 'Ei ilmoitettu'}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Sähköposti</p>
                {opponentContact?.email ? (
                  <a
                    href={`mailto:${opponentContact.email}`}
                    className="mt-1 inline-block text-sm font-semibold text-cyan-700 underline decoration-cyan-400 underline-offset-2 hover:text-cyan-900"
                  >
                    {opponentContact.email}
                  </a>
                ) : (
                  <p className="mt-1 text-sm font-semibold text-slate-900">Ei ilmoitettu</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    )}

    <MatchPlayRulesModal isOpen={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)} topic={helpModalTopic} />
    <CourseDialog
      open={pairCourseDialogOpen}
      courseForm={pairCourseDialogForm}
      saving={pairCourseDialogSaving}
      onClose={() => {
        if (!pairCourseDialogSaving) {
          setPairCourseDialogOpen(false);
        }
      }}
      onSubmit={handleSavePairCourseDialog}
      onFieldChange={handlePairCourseDialogFieldChange}
    />
    </>
  );
};

export default CompetitionCard;

